import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ReadinessWidget } from '../components/ReadinessWidget';
import { motion } from 'motion/react';
import { toast } from 'sonner';
import { BACKEND_URL } from '../config';
import { useOrganization } from '../context/OrganizationContext';
import { fetchWithOrg } from '../utils/api';
import { getAppLogo } from '../utils/usedApps';
import { connectApp, checkConnectionStatus, getSlug, getAuthProvider, getConnectLabel, openOAuthPopup } from '../utils/composioAuth';
import { CAPABILITY_LABELS } from '../utils/outcomeLabels';
import RunAgentModal from '../components/RunAgentModal';
import useActivation from '../hooks/useActivation';
import ActivationPanel from '../components/ActivationPanel';
import WorkerVersionsPanel from '../components/WorkerVersionsPanel';

import {
  ArrowLeft,
  ArrowRight,
  CaretDown,
  CheckCircle,
  CircleNotch,
  Clock,
  EnvelopeSimple,
  GearSix,
  Globe,
  Lightning,
  Pause,
  PencilSimple,
  Play,
  Robot,
  ShareNetwork,
  Sparkle,
  Sliders,
  PlugsConnected,
  PaperPlaneTilt,
  Upload,
  X,
  ShieldCheck,
  Wrench,
  HandPalm,
  HourglassMedium,
  Warning,
  Pulse,
  ArrowsClockwise,
  Info,
  CalendarBlank,
  FileText,
  Copy,
  LinkSimple,
  Trash,
  Plus,
  Code,
  FilePdf,
  FileDoc,
  FileXls,
  FileAudio,
  FileVideo,
  FileImage,
  FileMagnifyingGlass,
  DownloadSimple,
  FloppyDisk,
  ClipboardText,
  Eye,
  ChatCircle,
  Storefront,
} from '@phosphor-icons/react';
import DeploymentModal from '../components/DeploymentModal';
import OutputRenderer from '../components/OutputRenderer';
import AnalysisResultView from '../components/AnalysisResultView';
import TestEnquiryModal from '../components/TestEnquiryModal';
import ChatWorkerModal from '../components/ChatWorkerModal';
import SetupRequiredModal from '../components/SetupRequiredModal';
import { getWorkerIcon } from '../utils/workerIcons';

/* ═══════════════════════════════════════════════════════
   HELPERS
   ═══════════════════════════════════════════════════════ */

const STEP_STYLES = {
  trigger:   { bg: 'bg-[#243041]',  border: 'border-[rgba(20,184,166,0.15)]',  text: 'text-[#14B8A6]' },
  condition: { bg: 'bg-[#243041]',  border: 'border-[rgba(245,158,11,0.15)]', text: 'text-[#F59E0B]' },
  action:    { bg: 'bg-[#243041]',  border: 'border-[rgba(20,184,166,0.15)]', text: 'text-[#B0BAC5]' },
};

/** Workers ineligible for website embed (Orion, Voice S2S, internal-only) */
const _isWorkerInternalOnly = (w) => {
  if (!w) return true;
  const name = (w.name || '').toLowerCase();
  const type = (w.automation_type || '').toLowerCase();
  if (type === 'orion' || name.includes('orion')) return true;
  if (type === 'voice_s2s' || name.includes('voice s2s') || name.includes('voice-s2s')) return true;
  if (name.includes('internal') || name.includes('admin only')) return true;
  return false;
};

/** ─────────────────────────────────────────────────────────────
 *  Entry-point compatibility — only show surfaces that this
 *  worker can actually drive. See user-approved rule set.
 *  ───────────────────────────────────────────────────────────── */
const _workerType = (w) => (w?.automation_type || '').toLowerCase();
const _isVoiceWorker = (w) => _workerType(w).includes('voice');
const _isChatWorker = (w) => ['chat', 'chatbot', 'conversational', 'support_chat', 'website_chat'].includes(_workerType(w));
const _isFileWorker = (w) => ['file_analysis', 'file', 'analysis'].includes(_workerType(w));

/** Form entry-point fits when the worker can ingest a structured lead/enquiry payload. */
const _workerSupportsFormEntry = (w) => {
  if (!w || _isWorkerInternalOnly(w)) return false;
  if (_isVoiceWorker(w)) return false;
  if (_isChatWorker(w)) return false;
  if (_isFileWorker(w)) return false;
  return true;
};

/** Returns true if any step touches calendar / availability / booking. */
const _workerUsesCalendar = (w) => {
  const steps = w?.steps || [];
  return steps.some((s) => {
    const app = (s.app || '').toLowerCase();
    if (app.includes('calendar')) return true;
    const l = (s.label || '').toLowerCase();
    return l.includes('calendar') || l.includes('availab') || l.includes('schedul') || l.includes('book') || l.includes('appoint');
  });
};

/** Calendar entry-point only shows when the worker actually deals with calendars. */
const _workerSupportsCalendarEntry = (w) => {
  if (!w || _isWorkerInternalOnly(w)) return false;
  if (_isVoiceWorker(w) || _isChatWorker(w) || _isFileWorker(w)) return false;
  return _workerUsesCalendar(w);
};

/** Chat entry-point only shows when the worker can power a real-time conversation. */
const _workerSupportsChat = (w) => {
  if (!w || _isWorkerInternalOnly(w)) return false;
  if (_isVoiceWorker(w) || _isFileWorker(w)) return false;
  if (_isChatWorker(w)) return true;
  // Otherwise look for explicit conversational signals in step labels.
  const steps = w?.steps || [];
  return steps.some((s) => {
    const l = (s.label || '').toLowerCase();
    return l.includes('chat') || l.includes('conversation') || l.includes('respond to user') || l.includes('answer the user') || l.includes('reply to message') || l.includes('live chat');
  });
};

/** Apps whose records are surfaced to the Customer Portal (CRMs, billing, scheduling). */
const _PORTAL_APP_HINTS = ['hubspot', 'salesforce', 'pipedrive', 'zoho', 'stripe', 'quickbooks', 'xero', 'square'];
/** Step-label keywords that signal the worker writes a customer-tied record. */
const _PORTAL_STEP_KEYWORDS = ['enquir', 'lead', 'customer', 'job', 'quote', 'invoice', 'contact', 'booking', 'appointment'];

/**
 * Returns true if this worker writes records that would appear in the
 * Customer Portal (jobs / enquiries / quotes / messages tied to a customer email).
 */
const _workerProducesPortalRecords = (w) => {
  if (!w || _isWorkerInternalOnly(w)) return false;
  if (_isVoiceWorker(w)) return false; // voice workers don't write portal records directly
  const steps = w?.steps || [];
  if (!steps.length) return false;
  return steps.some((s) => {
    const app = (s.app || '').toLowerCase();
    if (_PORTAL_APP_HINTS.some(h => app.includes(h))) return true;
    const l = (s.label || '').toLowerCase();
    // Action verbs that signal a record write — avoid pure read/check labels
    const writes = l.includes('save') || l.includes('create') || l.includes('log') || l.includes('update') || l.includes('add');
    if (!writes) return false;
    return _PORTAL_STEP_KEYWORDS.some(k => l.includes(k));
  });
};

const TRUST_BADGE_CONFIG = {
  ready: { label: 'Ready', color: '#00E5C0', bg: 'rgba(0,229,192,0.08)', border: 'rgba(0,229,192,0.15)', Icon: ShieldCheck },
  needs_setup: { label: 'Needs setup', color: '#F59E0B', bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.12)', Icon: Wrench },
  assist_only: { label: 'Assist only', color: '#8B8FF5', bg: 'rgba(139,143,245,0.06)', border: 'rgba(139,143,245,0.12)', Icon: HandPalm },
  coming_soon: { label: 'Coming soon', color: '#7D8896', bg: 'rgba(255,255,255,0.02)', border: 'rgba(255,255,255,0.06)', Icon: HourglassMedium },
};

const getOutcomeHeadline = (auto) => {
  if (!auto) return 'Worker';
  const name = (auto.name || '').toLowerCase();
  const type = (auto.automation_type || '').toLowerCase();
  if (name.includes('enquir') || name.includes('customer') || name.includes('support') || type.includes('enquir'))
    return 'Respond to every new enquiry instantly';
  if (name.includes('lead') || name.includes('sales') || name.includes('follow'))
    return 'Never miss a lead again';
  if (name.includes('email') || name.includes('inbox'))
    return 'Handle every email without lifting a finger';
  if (name.includes('book') || name.includes('schedul') || name.includes('appoint'))
    return 'Turn enquiries into booked jobs automatically';
  if (name.includes('invoice') || name.includes('billing') || name.includes('payment'))
    return 'Process invoices and get paid faster';
  if (name.includes('review') || name.includes('feedback'))
    return 'Build your reputation on autopilot';
  if (name.includes('meeting') || name.includes('note') || name.includes('minute'))
    return 'Capture every meeting without taking notes';
  if (name.includes('report') || name.includes('analys') || name.includes('data'))
    return 'Get insights without crunching numbers';
  if (name.includes('social') || name.includes('post') || name.includes('content'))
    return 'Keep your content flowing automatically';
  if (name.includes('onboard') || name.includes('welcome'))
    return 'Welcome every new customer automatically';
  if (name.includes('contract') || name.includes('document') || name.includes('summary'))
    return 'Process documents in seconds, not hours';
  if (name.includes('quote') || name.includes('proposal'))
    return 'Send quotes before your competitors do';
  return auto.name || 'Worker';
};

const getPlainDescription = (worker) => {
  if (!worker?.steps?.length) return 'This worker is ready to be configured.';
  const labels = worker.steps.map((s) => s.label);
  return labels.join(', then ').replace(/,([^,]*)$/, ', and$1') + '.';
};

// Infer app from step label when step.app is not explicitly set
const inferAppFromStep = (step) => {
  if (step.app) return step.app;
  const label = (step.label || '').toLowerCase();
  if (label.includes('email') || label.includes('gmail') || label.includes('inbox')) return 'Gmail';
  if (label.includes('hubspot') || (label.includes('crm') && !label.includes('salesforce'))) return 'HubSpot';
  if (label.includes('salesforce')) return 'Salesforce';
  if (label.includes('calendar') || label.includes('availability') || label.includes('schedule') || label.includes('appointment')) return 'Google Calendar';
  if (label.includes('slack') || label.includes('channel')) return 'Slack';
  if (label.includes('sms') || label.includes('text message') || label.includes('twilio')) return 'Twilio';
  if (label.includes('whatsapp')) return 'WhatsApp';
  if (label.includes('stripe') || label.includes('payment') || label.includes('invoice')) return 'Stripe';
  if (label.includes('google sheet') || label.includes('spreadsheet')) return 'Google Sheets';
  if (label.includes('notion')) return 'Notion';
  if (label.includes('log activity') || label.includes('log ')) return 'HubSpot';
  return null;
};

const timeAgo = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const secs = Math.floor((Date.now() - d) / 1000);
  if (secs < 60) return 'just now';
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const TONE_OPTIONS = [
  { value: 'friendly', label: 'Friendly' },
  { value: 'professional', label: 'Professional' },
  { value: 'direct', label: 'Direct' },
  { value: 'casual', label: 'Casual' },
  { value: 'empathetic', label: 'Empathetic' },
];

const STYLE_OPTIONS = [
  { value: 'concise', label: 'Concise' },
  { value: 'detailed', label: 'Detailed' },
  { value: 'bullet-points', label: 'Bullet points' },
];

const DEFAULT_BEHAVIOR = {
  tone: ['friendly', 'professional'],
  response_style: 'concise',
  business_context: '',
  website_url: '',
};

/* ═══════════════════════════════════════════════════════
   EMAIL TRIGGER CARD
   ═══════════════════════════════════════════════════════ */
function EmailTriggerCard({ emailTriggerConfig, workerId, togglingEmail, onToggle, onSimulate, onPoll, gmailConnected, polling }) {
  const isEnabledForThis = emailTriggerConfig?.enabled && emailTriggerConfig?.worker_id === workerId;
  const isEnabledForOther = emailTriggerConfig?.enabled && emailTriggerConfig?.worker_id && emailTriggerConfig?.worker_id !== workerId;
  const needsGmail = isEnabledForThis && gmailConnected === false;

  return (
    <div
      className={`p-4 rounded-xl border transition-colors ${
        isEnabledForThis
          ? needsGmail
            ? 'bg-[rgba(245,158,11,0.04)] border-[rgba(245,158,11,0.2)]'
            : 'bg-[rgba(0,229,192,0.04)] border-[rgba(0,229,192,0.2)]'
          : 'bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.08)]'
      }`}
      data-testid="email-trigger-card"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-9 h-9 rounded-lg flex items-center justify-center overflow-hidden ${
            isEnabledForThis
              ? needsGmail ? 'bg-[rgba(245,158,11,0.12)]' : 'bg-[rgba(0,229,192,0.12)]'
              : 'bg-[rgba(255,255,255,0.04)]'
          }`}>
            <img src="https://www.gstatic.com/images/branding/product/1x/gmail_2020q4_32dp.png" alt="Gmail" className="w-5 h-5 object-contain" />
          </div>
          <div>
            <p className="text-sm font-medium text-white">Inbound Email Trigger</p>
            <p className="text-[11px] text-[#7D8896] mt-0.5">
              {isEnabledForThis
                ? needsGmail
                  ? 'Connect Gmail to start receiving real emails'
                  : 'New emails automatically trigger this worker'
                : isEnabledForOther
                ? `Active on "${emailTriggerConfig.worker_name || 'another worker'}"`
                : 'Route incoming emails to this worker'}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {isEnabledForThis && gmailConnected && (
            <button onClick={onPoll} disabled={polling}
              className="px-3 py-1.5 rounded-lg bg-[rgba(0,229,192,0.08)] border border-[rgba(0,229,192,0.15)] text-[11px] font-medium text-[#00E5C0] hover:bg-[rgba(0,229,192,0.15)] transition-colors disabled:opacity-50"
              data-testid="email-trigger-poll-btn">
              {polling ? <CircleNotch size={12} className="animate-spin inline mr-1" /> : null}
              {polling ? 'Checking...' : 'Check inbox'}
            </button>
          )}
          {isEnabledForThis && (
            <button onClick={onSimulate}
              className="px-3 py-1.5 rounded-lg bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[11px] font-medium text-[#B0B8C4] hover:text-white hover:border-[rgba(255,255,255,0.15)] transition-colors"
              data-testid="email-trigger-simulate-btn">
              Test with email
            </button>
          )}
          <button onClick={onToggle} disabled={togglingEmail} data-testid="email-trigger-toggle-btn"
            className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
              isEnabledForThis
                ? 'bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] text-[#B0B8C4] hover:text-[#EF4444] hover:border-[rgba(239,68,68,0.2)]'
                : 'bg-[#00E5C0] text-[#12161C] hover:bg-[#00CCB0]'
            }`}>
            {togglingEmail ? <CircleNotch size={14} className="animate-spin" />
              : isEnabledForThis ? 'Disable'
              : isEnabledForOther ? 'Switch to this worker'
              : 'Enable'}
          </button>
        </div>
      </div>

      {needsGmail && (
        <div className="mt-3 pt-3 border-t border-[rgba(245,158,11,0.1)]">
          <a href="/apps?connect=gmail" data-testid="connect-gmail-cta"
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-[rgba(245,158,11,0.08)] border border-[rgba(245,158,11,0.15)] text-[#F59E0B] text-xs font-medium hover:bg-[rgba(245,158,11,0.15)] transition-colors">
            <EnvelopeSimple size={14} weight="bold" />
            Connect Gmail to enable real email polling
            <ArrowRight size={12} />
          </a>
        </div>
      )}

      {isEnabledForThis && !needsGmail && emailTriggerConfig && (
        <div className="flex items-center gap-4 mt-3 pt-3 border-t border-[rgba(255,255,255,0.08)]">
          <span className="text-[10px] text-[#7D8896]">{emailTriggerConfig.emails_processed || 0} emails processed</span>
          {emailTriggerConfig.last_poll_at && (
            <span className="text-[10px] text-[#7D8896]">Last check: {timeAgo(emailTriggerConfig.last_poll_at)}</span>
          )}
          {gmailConnected && (
            <span className="inline-flex items-center gap-1 text-[10px] text-[#00E5C0]">
              <span className="w-1.5 h-1.5 rounded-full bg-[#00E5C0] animate-pulse" />
              Gmail connected
            </span>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   BEHAVIOR SETTINGS — All users
   ═══════════════════════════════════════════════════════ */
function BehaviorSettings({ worker, orgId, onUpdated }) {
  const behavior = { ...DEFAULT_BEHAVIOR, ...(worker.behavior || {}) };
  const [tone, setTone] = useState(behavior.tone || ['friendly', 'professional']);
  const [style, setStyle] = useState(behavior.response_style || 'concise');
  const [context, setContext] = useState(behavior.business_context || '');
  const [url, setUrl] = useState(behavior.website_url || '');
  const [saving, setSaving] = useState(false);
  const [fetchingUrl, setFetchingUrl] = useState(false);
  const [dirty, setDirty] = useState(false);

  const toggleTone = (val) => {
    setTone(prev => prev.includes(val) ? prev.filter(t => t !== val) : [...prev, val]);
    setDirty(true);
  };

  const saveBehavior = async () => {
    setSaving(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/automations/${worker.id}`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            behavior: { tone, response_style: style, business_context: context, website_url: url },
          }),
        },
        orgId
      );
      if (res.ok) {
        toast.success('Behavior settings saved');
        setDirty(false);
        onUpdated?.();
      } else {
        toast.error('Failed to save');
      }
    } catch { toast.error('Error saving'); }
    finally { setSaving(false); }
  };

  const fetchWebsite = async () => {
    if (!url) return;
    setFetchingUrl(true);
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/command/extract-context`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url }),
        },
        orgId
      );
      if (res.ok) {
        const data = await res.json();
        if (data.business_context) {
          setContext(data.business_context);
          setDirty(true);
          toast.success('Business context extracted from website');
        } else {
          toast.error('No business context could be extracted');
        }
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not extract context from website');
      }
    } catch { toast.error('Could not fetch website'); }
    finally { setFetchingUrl(false); }
  };

  return (
    <div className="space-y-5" data-testid="behavior-settings">
      {/* Tone */}
      <div>
        <label className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] block mb-2">Tone of voice</label>
        <div className="flex flex-wrap gap-2">
          {TONE_OPTIONS.map((opt) => (
            <button key={opt.value} onClick={() => toggleTone(opt.value)} data-testid={`tone-${opt.value}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                tone.includes(opt.value)
                  ? 'bg-[rgba(0,229,192,0.1)] border-[rgba(0,229,192,0.3)] text-[#00E5C0]'
                  : 'bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.08)] text-[#7D8896] hover:text-[#B0B8C4] hover:border-[rgba(255,255,255,0.12)]'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Response style */}
      <div>
        <label className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] block mb-2">Response style</label>
        <div className="flex gap-2">
          {STYLE_OPTIONS.map((opt) => (
            <button key={opt.value} onClick={() => { setStyle(opt.value); setDirty(true); }} data-testid={`style-${opt.value}`}
              className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${
                style === opt.value
                  ? 'bg-[rgba(0,229,192,0.1)] border-[rgba(0,229,192,0.3)] text-[#00E5C0]'
                  : 'bg-[rgba(255,255,255,0.02)] border-[rgba(255,255,255,0.08)] text-[#7D8896] hover:text-[#B0B8C4] hover:border-[rgba(255,255,255,0.12)]'
              }`}>
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Custom Instructions */}
      <div>
        <label className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] block mb-2">Custom instructions</label>
        <textarea value={context} onChange={(e) => { setContext(e.target.value); setDirty(true); }}
          rows={4} placeholder="Add anything specific this worker should know (tone, rules, priorities, exclusions...)"
          data-testid="behavior-context"
          className="w-full px-4 py-3 bg-[#1F2937] border border-[rgba(255,255,255,0.08)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#5C6778] focus:outline-none focus:border-[rgba(20,184,166,0.5)] transition-colors resize-none" />
      </div>

      {/* Save */}
      {dirty && (
        <button onClick={saveBehavior} disabled={saving} data-testid="save-behavior-btn"
          className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-[#00E5C0] text-[#12161C] text-sm font-semibold hover:bg-[#00CCB0] transition-colors disabled:opacity-50">
          {saving ? <CircleNotch size={14} className="animate-spin" /> : <CheckCircle size={14} weight="bold" />}
          {saving ? 'Saving...' : 'Save behavior'}
        </button>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════
   WORKER DETAIL — Service OS Automation
   ═══════════════════════════════════════════════════════ */
export default function WorkerDetail() {
  const { workerId } = useParams();
  const navigate = useNavigate();
  const location = window.location;
  const navState = typeof window !== 'undefined' && window.history?.state?.usr;
  const { activeOrganization, isPartnerPro } = useOrganization();
  const orgId = activeOrganization?.organization_id;

  const [worker, setWorker] = useState(null);
  const [loading, setLoading] = useState(true);
  const [executing, setExecuting] = useState(false);
  const [executions, setExecutions] = useState([]);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showBehavior, setShowBehavior] = useState(true);
  const [showTestInput, setShowTestInput] = useState(false);
  const [testEnquiry, setTestEnquiry] = useState('');
  const [testFile, setTestFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [refText, setRefText] = useState('');
  const [refFile, setRefFile] = useState(null);
  const [savedRefText, setSavedRefText] = useState('');
  const [savedRefFileName, setSavedRefFileName] = useState('');
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showRunAgentModal, setShowRunAgentModal] = useState(false);
  const [showDeployModal, setShowDeployModal] = useState(false);
  const [showTestEnquiry, setShowTestEnquiry] = useState(false);
  const [showSetupRequired, setShowSetupRequired] = useState(false);
  const [profileCompletion, setProfileCompletion] = useState(null);
  const [workerTrust, setWorkerTrust] = useState(null);

  const [emailTriggerConfig, setEmailTriggerConfig] = useState(null);
  const [togglingEmail, setTogglingEmail] = useState(false);
  const [gmailConnected, setGmailConnected] = useState(null);
  const [polling, setPolling] = useState(false);

  const [expandedExec, setExpandedExec] = useState(null);

  // Trigger config state (Phase 6.5)
  const [showTriggers, setShowTriggers] = useState(true);
  const [showFormConfig, setShowFormConfig] = useState(false);
  const [showCalConfig, setShowCalConfig] = useState(false);
  const [formTrigger, setFormTrigger] = useState(null);
  const [calendarTrigger, setCalendarTrigger] = useState(null);
  const [formTriggerLoading, setFormTriggerLoading] = useState(false);
  const [calendarTriggerLoading, setCalendarTriggerLoading] = useState(false);
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [formMappingDraft, setFormMappingDraft] = useState([]);
  const [savingFormTrigger, setSavingFormTrigger] = useState(false);
  const [savingCalTrigger, setSavingCalTrigger] = useState(false);

  // Live system run state
  const [liveRun, setLiveRun] = useState(null);
  const [liveRunLoading, setLiveRunLoading] = useState(true);

  // Gate worker actions behind profile completion
  const handleGatedAction = (action) => {
    // Only gate if profile explicitly incomplete (not when loading or for file/chat workers)
    const isIncomplete = profileCompletion && profileCompletion.status === 'incomplete' && profileCompletion.percentage < 50;
    if (isIncomplete && runtimeMode === 'process') {
      setShowSetupRequired(true);
      return;
    }
    action();
  };


  // Toggle inline output preview
  const toggleExecPreview = (execId) => {
    setExpandedExec(prev => prev === execId ? null : execId);
  };

  // Composio connection state
  const [connectedApps, setConnectedApps] = useState(new Set());
  const [connectingApp, setConnectingApp] = useState(null); // slug currently connecting
  const [unlockedCapabilities, setUnlockedCapabilities] = useState(null); // post-connect unlock UX
  const [readinessRefreshKey, setReadinessRefreshKey] = useState(0);
  const { triggerActivation, activationData, dismiss: dismissActivation } = useActivation();

  const fetchEmailConfig = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/email-trigger/config`, {}, orgId);
      if (res.ok) setEmailTriggerConfig(await res.json());
    } catch {}
  }, [orgId]);

  const fetchGmailStatus = useCallback(async () => {
    if (!orgId) return;
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/email-trigger/gmail-status`, {}, orgId);
      if (res.ok) {
        const data = await res.json();
        setGmailConnected(data.connected);
      }
    } catch { setGmailConnected(false); }
  }, [orgId]);

  useEffect(() => { fetchEmailConfig(); fetchGmailStatus(); }, [fetchEmailConfig, fetchGmailStatus]);

  // Fetch form and calendar trigger configs (Phase 6.5)
  const fetchFormTrigger = useCallback(async () => {
    if (!orgId || !workerId) return;
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/form-triggers`, {}, orgId);
      if (res.ok) {
        const data = await res.json();
        const match = (data.form_triggers || []).find(t => t.worker_id === workerId);
        setFormTrigger(match || null);
        if (match) setFormMappingDraft(match.field_mappings || []);
      }
    } catch {}
  }, [orgId, workerId]);

  const fetchCalendarTrigger = useCallback(async () => {
    if (!orgId || !workerId) return;
    try {
      const [trigRes, connRes] = await Promise.all([
        fetchWithOrg(`${BACKEND_URL}/api/service-os/calendar-triggers`, {}, orgId),
        fetchWithOrg(`${BACKEND_URL}/api/service-os/calendar-triggers/status/connection`, {}, orgId),
      ]);
      if (trigRes.ok) {
        const data = await trigRes.json();
        const match = (data.calendar_triggers || []).find(t => t.worker_id === workerId);
        setCalendarTrigger(match || null);
      }
      if (connRes.ok) {
        const data = await connRes.json();
        setCalendarConnected(data.connected);
      }
    } catch {}
  }, [orgId, workerId]);

  useEffect(() => { fetchFormTrigger(); fetchCalendarTrigger(); }, [fetchFormTrigger, fetchCalendarTrigger]);

  // Fetch profile completion and trust audit for worker gating
  useEffect(() => {
    if (!orgId) return;
    (async () => {
      try {
        const [compRes, trustRes] = await Promise.all([
          fetchWithOrg(`${BACKEND_URL}/api/service-os/profile/completion`, {}, orgId),
          fetchWithOrg(`${BACKEND_URL}/api/service-os/automations/trust-audit`, {}, orgId),
        ]);
        if (compRes.ok) setProfileCompletion(await compRes.json());
        if (trustRes.ok) {
          const trustData = await trustRes.json();
          const match = (trustData.audit || []).find(a => a.worker_id === workerId);
          if (match) setWorkerTrust(match);
        }
      } catch {}
    })();
  }, [orgId, workerId]);

  // Fetch Composio connection status
  const fetchConnections = useCallback(async () => {
    if (!orgId) return;
    try {
      const data = await checkConnectionStatus(orgId);
      setConnectedApps(new Set(data.connected_apps || []));
    } catch {}
  }, [orgId]);

  useEffect(() => { fetchConnections(); }, [fetchConnections]);

  // Handle Composio OAuth connection for an app
  const handleConnectApp = async (appName) => {
    const slug = getSlug(appName);
    setConnectingApp(slug);
    try {
      const callbackUrl = `${window.location.origin}/oauth/callback?connected=${slug}`;
      const data = await connectApp(appName, orgId, callbackUrl);
      if (data.redirect_url) {
        toast.info(`Opening ${appName} authorization...`);
        const popupResult = await openOAuthPopup(data.redirect_url);
        // After popup closes (or postMessage received), re-check connection status
        toast.loading(`Checking ${appName} connection...`, { id: 'conn-check' });
        await new Promise(r => setTimeout(r, 2000));
        await fetchConnections();
        toast.dismiss('conn-check');

        let connected = connectedApps.has(slug);
        if (!connected) {
          // Re-check after another delay (Composio may need a moment)
          await new Promise(r => setTimeout(r, 3000));
          const freshData = await checkConnectionStatus(orgId);
          const freshSet = new Set(freshData.connected_apps || []);
          setConnectedApps(freshSet);
          connected = freshSet.has(slug);
        }

        if (connected) {
          toast.success(`${appName} connected successfully!`);
          triggerActivation(slug);
          // Fetch unlocked capabilities for unlock UX
          try {
            const capsRes = await fetch(
              `${BACKEND_URL}/api/capabilities/unlocked-by/${slug}`,
              { credentials: 'include' }
            );
            if (capsRes.ok) {
              const capsData = await capsRes.json();
              if (capsData.capabilities?.length > 0) {
                setUnlockedCapabilities({
                  provider: appName,
                  capabilities: capsData.capabilities,
                });
                // Auto-dismiss after 8 seconds
                setTimeout(() => setUnlockedCapabilities(null), 8000);
              }
            }
          } catch {}
          // Refresh live run data and trigger-specific status
          fetchLiveRun();
          fetchGmailStatus();
          // Refresh readiness widget to update status
          setReadinessRefreshKey(k => k + 1);
        } else {
          toast.info(`Authorization completed. ${appName} may take a moment to activate.`);
          // Still refresh readiness in case it did connect
          setReadinessRefreshKey(k => k + 1);
        }
      }
    } catch (err) {
      toast.error(err.message || `Failed to connect ${appName}`);
    } finally {
      setConnectingApp(null);
    }
  };

  const handleToggleEmailTrigger = async () => {
    setTogglingEmail(true);
    const isCurrentlyEnabled = emailTriggerConfig?.enabled && emailTriggerConfig?.worker_id === workerId;
    try {
      const res = await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/email-trigger/config`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(isCurrentlyEnabled
            ? { worker_id: workerId, enabled: false }
            : { worker_id: workerId, enabled: true }),
        },
        orgId
      );
      if (res.ok) {
        toast.success(isCurrentlyEnabled ? 'Email trigger disabled' : 'Email trigger enabled');
        fetchEmailConfig();
      }
    } catch { toast.error('Error updating email trigger'); }
    finally { setTogglingEmail(false); }
  };

  // Form trigger handlers (Phase 6.5)
  const handleCreateFormTrigger = async () => {
    setSavingFormTrigger(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/form-triggers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'Website Form', worker_id: workerId, field_mappings: formMappingDraft }),
      }, orgId);
      if (res.ok) { toast.success('Form trigger created'); fetchFormTrigger(); }
      else toast.error('Failed to create form trigger');
    } catch { toast.error('Error creating form trigger'); }
    finally { setSavingFormTrigger(false); }
  };

  const handleUpdateFormTrigger = async (updates) => {
    if (!formTrigger) return;
    setSavingFormTrigger(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/form-triggers/${formTrigger.form_trigger_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      }, orgId);
      if (res.ok) { toast.success('Form trigger updated'); fetchFormTrigger(); }
      else toast.error('Failed to update');
    } catch { toast.error('Error updating form trigger'); }
    finally { setSavingFormTrigger(false); }
  };

  const handleDeleteFormTrigger = async () => {
    if (!formTrigger) return;
    setSavingFormTrigger(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/form-triggers/${formTrigger.form_trigger_id}`, {
        method: 'DELETE',
      }, orgId);
      if (res.ok) { toast.success('Form trigger removed'); setFormTrigger(null); setFormMappingDraft([]); }
    } catch { toast.error('Error removing form trigger'); }
    finally { setSavingFormTrigger(false); }
  };

  // Calendar trigger handlers
  const handleCreateCalendarTrigger = async () => {
    setSavingCalTrigger(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/calendar-triggers`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: 'Calendar Trigger', worker_id: workerId, event_types: ['created', 'updated', 'cancelled'] }),
      }, orgId);
      if (res.ok) { toast.success('Calendar trigger created'); fetchCalendarTrigger(); }
      else toast.error('Failed to create calendar trigger');
    } catch { toast.error('Error creating calendar trigger'); }
    finally { setSavingCalTrigger(false); }
  };

  const handleUpdateCalendarTrigger = async (updates) => {
    if (!calendarTrigger) return;
    setSavingCalTrigger(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/calendar-triggers/${calendarTrigger.calendar_trigger_id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      }, orgId);
      if (res.ok) { toast.success('Calendar trigger updated'); fetchCalendarTrigger(); }
      else toast.error('Failed to update');
    } catch { toast.error('Error updating calendar trigger'); }
    finally { setSavingCalTrigger(false); }
  };

  const handleDeleteCalendarTrigger = async () => {
    if (!calendarTrigger) return;
    setSavingCalTrigger(true);
    try {
      const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/calendar-triggers/${calendarTrigger.calendar_trigger_id}`, {
        method: 'DELETE',
      }, orgId);
      if (res.ok) { toast.success('Calendar trigger removed'); setCalendarTrigger(null); }
    } catch { toast.error('Error removing calendar trigger'); }
    finally { setSavingCalTrigger(false); }
  };

  const fetchWorker = useCallback(async () => {
    if (!orgId) return;
    try {
      setLoading(true);
      // Use unified Worker API
      const res = await fetchWithOrg(`${BACKEND_URL}/api/workers/${workerId}`, {}, orgId);
      if (res.ok) {
        const data = await res.json();
        const w = data.worker || data;
        // Map canonical Worker shape to component expectations
        const auto = {
          ...w,
          agent_id: w.configuration?.agent_id || null,
          outcome_category: w.configuration?.outcome || '',
          automation_type: w.automation_type || w.trigger || w.type || 'event',
          worker_config: w.worker_config || {},
          requires_review: w.configuration?.requires_review || false,
          tool_preferences: Array.isArray(w.tools) ? w.tools : Object.keys(w.tools || {}),
          behavior: w.configuration?.behavior || {},
          outcome: w.configuration?.outcome || '',
          org_id: w.metadata?.org_id || orgId,
        };
        setWorker(auto);
      } else {
        // Fallback to legacy API for workers not yet in unified registry
        const legacyRes = await fetchWithOrg(`${BACKEND_URL}/api/service-os/automations/${workerId}`, {}, orgId);
        if (legacyRes.ok) {
          const data = await legacyRes.json();
          const auto = data.automation || data;
          if (!auto.agent_id) {
            try {
              const agentsRes = await fetchWithOrg(`${BACKEND_URL}/api/agents`, {}, orgId);
              if (agentsRes.ok) {
                const agentsData = await agentsRes.json();
                const agents = agentsData.agents || [];
                const match = agents.find(a =>
                  a.name === auto.name ||
                  a.agent_id === workerId ||
                  a.id === workerId
                );
                if (match) auto.agent_id = match.agent_id || match.id;
              }
            } catch {}
          }
          setWorker(auto);
        } else {
          toast.error('Worker not found');
          navigate('/dashboard');
        }
      }
    } catch { navigate('/dashboard'); }
    finally { setLoading(false); }
  }, [workerId, orgId, navigate]);

  const fetchExecutions = useCallback(async () => {
    if (!orgId) return;
    try {
      // Use unified Worker API for execution history
      const res = await fetchWithOrg(`${BACKEND_URL}/api/workers/${workerId}/runs?limit=5`, {}, orgId);
      if (res.ok) {
        const data = await res.json();
        setExecutions(data.runs || []);
      } else {
        // Fallback to legacy API
        const legacyRes = await fetchWithOrg(`${BACKEND_URL}/api/service-os/automations/${workerId}/executions?limit=5`, {}, orgId);
        if (legacyRes.ok) {
          const data = await legacyRes.json();
          setExecutions(data.executions || []);
        }
      }
    } catch {}
  }, [workerId, orgId]);

  useEffect(() => { fetchWorker(); fetchExecutions(); }, [fetchWorker, fetchExecutions]);

  // Fetch latest system run involving this worker
  const fetchLiveRun = useCallback(async () => {
    if (!orgId || !workerId) { setLiveRunLoading(false); return; }
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/system-runs/latest-for-worker/${workerId}`,
        { credentials: 'include' }
      );
      if (res.ok) {
        const data = await res.json();
        setLiveRun(data.run || null);
      }
    } catch {}
    finally { setLiveRunLoading(false); }
  }, [orgId, workerId]);

  useEffect(() => { fetchLiveRun(); }, [fetchLiveRun]);

  // Auto-refresh for active system runs
  useEffect(() => {
    if (!liveRun) return;
    const isActive = ['running', 'waiting', 'queued', 'retrying'].includes(liveRun.status);
    if (!isActive) return;
    const interval = setInterval(fetchLiveRun, 4000);
    return () => clearInterval(interval);
  }, [liveRun, fetchLiveRun]);

  // Auto-open test enquiry if navigated from setup completion
  useEffect(() => {
    if (navState?.openTestEnquiry && worker && !loading) {
      openRunExperience();
    }
  }, [navState, worker, loading]);

  const workerType = (worker?.automation_type || '').toLowerCase();
  const stepsHaveFileInput = (worker?.steps || []).some(s => {
    const l = (s.label || '').toLowerCase();
    return (s.type === 'input' || s.type === 'trigger') && (l.includes('file') || l.includes('upload') || l.includes('document') || l.includes('submission') || l.includes('pdf'));
  });
  const isFileWorker = workerType.includes('file') || workerType.includes('analysis') || workerType.includes('document') || stepsHaveFileInput;
  const linkedAgentId = worker?.agent_id;
  const wc = worker?.worker_config || {};
  const refInputType = wc.ref_input_type || 'text';
  const refRequired = wc.ref_required || false;
  const showRefText = refInputType === 'text' || refInputType === 'text_and_file';
  const showRefFile = refInputType === 'file' || refInputType === 'text_and_file';

  /* ─── Runtime Resolver ───
     Determines the correct run experience based on the worker's
     actual required inputs and outputs:
       'file'           → file/evaluation modal
       'chat'           → chat conversation modal
       'process'        → no modal, direct execution
       'process_export' → no modal, execute then show export
  */
  const runtimeMode = (() => {
    // 1. File workers — require file upload
    if (isFileWorker) return 'file';
    if ((wc.acceptedTypes || []).length > 0) return 'file';

    // 2. Check steps for input signals (trigger/input steps may indicate file upload)
    const steps = worker?.steps || [];
    const hasFileInput = steps.some(s => {
      const l = (s.label || '').toLowerCase();
      return (s.type === 'input' || s.type === 'trigger') && (l.includes('file') || l.includes('upload') || l.includes('document') || l.includes('submission') || l.includes('pdf'));
    });
    if (hasFileInput) return 'file';

    // 3. Chat workers — require conversational input
    const isChatType = workerType.includes('chat') || workerType.includes('conversation');
    const hasChatInput = steps.some(s => {
      const l = (s.label || '').toLowerCase();
      return s.type === 'input' && (l.includes('chat') || l.includes('message') || l.includes('enquir') || l.includes('question'));
    });
    if (isChatType || hasChatInput) return 'chat';

    // 4. Event/trigger workers with no explicit input — process only
    const isEventType = workerType.includes('event') || workerType.includes('trigger');
    const hasFileOutput = steps.some(s => {
      const l = (s.label || '').toLowerCase();
      return s.type === 'output' && (l.includes('file') || l.includes('export') || l.includes('download') || l.includes('pdf') || l.includes('report'));
    });
    if (isEventType) return hasFileOutput ? 'process_export' : 'process';

    // 4b. Template workers with trigger/action steps but no manual input — process
    const hasTriggerSteps = steps.some(s => s.type === 'trigger');
    const hasActionSteps = steps.some(s => s.type === 'action');
    if ((workerType === 'template' || workerType === '') && hasTriggerSteps && hasActionSteps) {
      return hasFileOutput ? 'process_export' : 'process';
    }

    // 5. Operator/voice workers — default to chat for testing
    if (workerType.includes('voice') || workerType.includes('operator')) return 'chat';

    // 6. Fallback: if worker has no defined input steps, it's process-only
    const hasInputStep = steps.some(s => s.type === 'input' || s.type === 'trigger');
    if (!hasInputStep && steps.length > 0) return hasFileOutput ? 'process_export' : 'process';

    // 7. Final fallback: template workers with trigger steps → process; otherwise chat for testing
    if (hasInputStep && !hasChatInput && !hasFileInput) return 'process';
    return 'chat';
  })();


  // Load saved reference material from worker_config
  useEffect(() => {
    if (wc.saved_reference_text) {
      setSavedRefText(wc.saved_reference_text);
      setRefText(wc.saved_reference_text);
    }
    if (wc.saved_reference_file_name) {
      setSavedRefFileName(wc.saved_reference_file_name);
    }
  }, [wc.saved_reference_text, wc.saved_reference_file_name]);

  // Save reference material to worker_config (persists across sessions)
  const saveReferenceToWorker = async (text, fileName) => {
    try {
      const updates = { ...wc, saved_reference_text: text || '', saved_reference_file_name: fileName || '' };
      await fetchWithOrg(
        `${BACKEND_URL}/api/service-os/automations/${workerId}`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ worker_config: updates }) },
        orgId
      );
      setSavedRefText(text || '');
      setSavedRefFileName(fileName || '');
    } catch { /* silent */ }
  };


  const handleRun = async (enquiryText) => {
    // File workers always use the inline test modal (file upload + reference material)
    if (runtimeMode === 'file') {
      if (!enquiryText?.trim() && !testFile) {
        setShowTestInput(true);
        return;
      }
    } else if (linkedAgentId) {
      setShowRunAgentModal(true);
      return;
    }
    // Inline execution
    if (runtimeMode === 'file' && !enquiryText?.trim() && !testFile) {
      setShowTestInput(true);
      return;
    }
    setShowTestInput(false);
    setExecuting(true);
    try {
      let res;
      if (testFile) {
        // File upload execution — use dedicated multipart endpoint
        const formData = new FormData();
        formData.append('file', testFile);
        if (enquiryText?.trim()) formData.append('instructions', enquiryText.trim());
        // Include runtime reference material
        if (refText.trim()) formData.append('reference_text', refText.trim());
        if (refFile) formData.append('reference_file', refFile);
        res = await fetchWithOrg(
          `${BACKEND_URL}/api/service-os/automations/${workerId}/execute-file`,
          { method: 'POST', body: formData },
          orgId
        );
      } else {
        // Text / process execution
        res = await fetchWithOrg(
          `${BACKEND_URL}/api/service-os/automations/${workerId}/execute`,
          { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ test_input: { enquiry: enquiryText?.trim() || 'Run workflow', customer_name: 'Test Customer' } }) },
          orgId
        );
      }
      if (res.ok) {
        const data = await res.json().catch(() => ({}));
        toast.success(runtimeMode === 'file' ? 'Analysis complete' : 'Worker run complete');
        setAnalysisResult({
          output: data.final_output || data.summary || 'Completed successfully.',
          summary: data.summary || '',
          runId: data.run_id || data.execution_id || '',
          fileName: testFile?.name || '',
        });
        setShowTestInput(false);
        setTestFile(null);
        if (runtimeMode === 'file' && (refText.trim() || refFile)) {
          saveReferenceToWorker(refText.trim(), refFile?.name || savedRefFileName);
        }
        fetchWorker();
        fetchExecutions();
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Execution failed');
      }
    } catch { toast.error('Error running worker'); }
    finally { setExecuting(false); }
  };

  // Runtime-aware: open the correct experience
  const openRunExperience = () => {
    handleGatedAction(() => {
      switch (runtimeMode) {
        case 'file':
          setShowTestInput(true);
          break;
        case 'chat':
          setShowTestEnquiry(true);
          break;
        case 'process':
        case 'process_export':
          // Direct execution — no modal needed
          handleRun('Run workflow');
          break;
        default:
          setShowTestEnquiry(true);
      }
    });
  };

  const handleToggleStatus = async () => {
    const newStatus = worker.status === 'active' ? 'paused' : 'active';
    try {
      const res = await fetch(
        `${BACKEND_URL}/api/workers/${workerId}/status`,
        { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ status: newStatus }), credentials: 'include' }
      );
      if (res.ok) {
        toast.success(`Worker ${newStatus === 'active' ? 'activated' : 'paused'}`);
        fetchWorker();
      }
    } catch { toast.error('Error updating worker'); }
  };

  if (loading) {
    return (
      <div className="container mx-auto flex items-center justify-center min-h-[60vh]">
        <CircleNotch size={48} className="animate-spin text-[#00E5C0]" />
      </div>
    );
  }

  if (!worker) {
    return (
      <div className="container mx-auto px-6 pt-12 text-center">
        <p className="text-white text-lg mb-4">Worker not found</p>
        <button onClick={() => navigate('/dashboard')} className="text-[#00E5C0] text-sm hover:underline">
          <ArrowLeft size={14} className="inline mr-1" /> Back to Workers
        </button>
      </div>
    );
  }

  const isActive = worker.status === 'active';
  const headline = getOutcomeHeadline(worker);
  const description = getPlainDescription(worker);
  const hasRuns = executions.length > 0 || (worker.run_count && worker.run_count > 0);

  return (
    <div className="container mx-auto max-w-4xl">
      {/* Back */}
      <button onClick={() => navigate('/dashboard')}
        className="flex items-center gap-2 text-[#B0B8C4] hover:text-white mb-6 transition-colors text-sm"
        data-testid="back-to-workers">
        <ArrowLeft size={16} /> Back to Workers
      </button>

      {/* ═══════ HEADER ═══════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-6 mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)]" data-testid="worker-header">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-2">
              <img
                src={getWorkerIcon(worker.outcome_category, worker.name)}
                alt=""
                className="w-12 h-12 rounded-xl object-cover flex-shrink-0"
                data-testid="worker-detail-icon"
              />
              <div>
                <h1 className="text-xl font-bold text-white" data-testid="worker-headline">{headline}</h1>
                <p className="text-xs text-[#7D8896] mt-0.5">{worker.name}</p>
              </div>
            </div>
            {/* Outcome-first description */}
            <p className="text-sm font-medium text-[#F0F4F8] mt-3 leading-relaxed" data-testid="worker-plain-description">
              {description.split('.')[0]}.
            </p>
            {description.split('.').length > 1 && (
              <p className="text-[13px] text-[#7D8896] mt-1 leading-relaxed">
                {description.split('.').slice(1).join('.').trim()}
              </p>
            )}
            <div className="flex items-center gap-2 flex-wrap mt-3">
              <span data-testid="worker-status-badge"
                className={`px-2.5 py-1 rounded-full text-[11px] font-semibold ${
                  isActive
                    ? 'bg-[rgba(0,229,192,0.1)] text-[#00E5C0] border border-[rgba(0,229,192,0.2)]'
                    : 'bg-[rgba(255,255,255,0.04)] text-[#7D8896] border border-[rgba(255,255,255,0.08)]'
                }`}>
                {isActive ? 'Running' : worker.status === 'paused' ? 'Paused' : 'Draft'}
              </span>
              {workerTrust && (() => {
                const cfg = TRUST_BADGE_CONFIG[workerTrust.classification];
                if (!cfg) return null;
                const { Icon: TIcon } = cfg;
                return (
                  <span
                    data-testid={`worker-trust-badge-${workerTrust.classification}`}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border"
                    style={{ color: cfg.color, background: cfg.bg, borderColor: cfg.border }}
                    title={workerTrust.reason}
                  >
                    <TIcon size={11} weight="bold" />
                    {cfg.label}
                  </span>
                );
              })()}
              {_workerProducesPortalRecords(worker) && (
                <button
                  type="button"
                  onClick={() => navigate('/settings?tab=portal')}
                  data-testid="worker-portal-visible-badge"
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-medium border bg-[rgba(99,102,241,0.06)] border-[rgba(99,102,241,0.18)] text-[#A5B4FC] hover:bg-[rgba(99,102,241,0.1)] hover:text-[#C7D2FE] transition-colors"
                  title="This worker creates customer-tied records (jobs, enquiries, quotes) that appear in your Customer Portal. Click to manage portal settings."
                >
                  <Storefront size={11} weight="duotone" />
                  Visible in Customer Portal
                </button>
              )}
              {worker.run_count > 0 && (
                <span className="text-[10px] text-[#7D8896]">{worker.run_count} tasks completed</span>
              )}
            </div>
            {/* Tool stack line */}
            {worker.tool_preferences?.length > 0 && (
              <div className="mt-2 space-y-1.5" data-testid="worker-tool-stack">
                <div className="flex items-center gap-2 flex-wrap">
                  <PlugsConnected size={12} className="text-[#5C6778] shrink-0" />
                  <span className="text-[11px] text-[#7D8896]">Using your tools:</span>
                  {worker.tool_preferences.map((tool) => {
                    const slug = tool.toLowerCase().replace(/\s+/g, '');
                    const isConnected = connectedApps.has(slug) || connectedApps.has(tool.toLowerCase().replace(/\s+/g, '-'));
                    return (
                      <span key={tool}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                          isConnected
                            ? 'text-[#00E5C0] bg-[rgba(0,229,192,0.04)] border-[rgba(0,229,192,0.12)]'
                            : 'text-[#F59E0B] bg-[rgba(245,158,11,0.04)] border-[rgba(245,158,11,0.1)]'
                        }`}
                        data-testid={`tool-status-${tool.toLowerCase().replace(/\s+/g, '-')}`}
                      >
                        {isConnected ? <CheckCircle size={9} weight="fill" /> : <Warning size={9} />}
                        {tool}
                        {!isConnected && (
                          <button
                            onClick={(e) => { e.stopPropagation(); handleConnectApp(tool); }}
                            className="ml-0.5 text-[9px] underline text-[#F59E0B] hover:text-[#FBBF24]"
                            disabled={connectingApp === getSlug(tool)}
                            data-testid={`connect-${tool.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {connectingApp === getSlug(tool) ? '...' : 'Connect'}
                          </button>
                        )}
                      </span>
                    );
                  })}
                </div>
                {/* Draft/Limited transparency label */}
                {(() => {
                  const toolSteps = worker.tool_preferences || [];
                  const unconnected = toolSteps.filter(t => {
                    const slug = t.toLowerCase().replace(/\s+/g, '');
                    return !connectedApps.has(slug) && !connectedApps.has(t.toLowerCase().replace(/\s+/g, '-'));
                  });
                  if (unconnected.length === toolSteps.length && toolSteps.length > 0) {
                    return (
                      <div className="flex items-center gap-2 pl-5" data-testid="worker-draft-behavior-label">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-[rgba(139,143,245,0.04)] border-[rgba(139,143,245,0.12)] text-[#8B8FF5]">
                          Draft behavior
                        </span>
                        <span className="text-[10px] text-[#5C6778]">Generates drafts for your review. Connect tools for live actions.</span>
                      </div>
                    );
                  }
                  if (unconnected.length > 0) {
                    return (
                      <div className="flex items-center gap-2 pl-5" data-testid="worker-limited-behavior-label">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border bg-[rgba(245,158,11,0.04)] border-[rgba(245,158,11,0.12)] text-[#F59E0B]">
                          Limited
                        </span>
                        <span className="text-[10px] text-[#5C6778]">{unconnected.join(', ')} not yet connected.</span>
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {/* Run mode indicator */}
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[10px] font-medium border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.02)] text-[#7D8896]" data-testid="run-mode-badge">
              {runtimeMode === 'file' && <><Upload size={10} className="text-[#00E5C0]" /> File upload</>}
              {runtimeMode === 'chat' && <><ChatCircle size={10} className="text-[#00E5C0]" /> Conversation</>}
              {runtimeMode === 'process' && <><Lightning size={10} className="text-[#00E5C0]" /> Direct process</>}
              {runtimeMode === 'process_export' && <><Lightning size={10} className="text-[#00E5C0]" /> Process + export</>}
            </span>
            {isActive && (
              <button onClick={openRunExperience} disabled={executing} data-testid="run-worker-btn"
                className="flex items-center gap-1.5 px-5 py-2.5 rounded-lg bg-[#14B8A6] hover:bg-[#0D9488] text-white text-sm font-semibold transition-colors disabled:opacity-50 shadow-[0_2px_8px_rgba(20,184,166,0.25)]">
                {executing ? <CircleNotch size={16} className="animate-spin" /> : <Play size={16} weight="fill" />}
                {executing ? 'Running...' : 'Run Worker'}
              </button>
            )}
            <button onClick={() => navigate('/workers/new', { state: { editWorker: { id: workerId, name: worker.name, automation_type: worker.automation_type, steps: worker.steps, worker_config: worker.worker_config || wc } } })}
              data-testid="edit-workflow-btn"
              className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg text-sm font-medium border border-[rgba(255,255,255,0.1)] bg-transparent text-[#F0F4F8] hover:bg-[rgba(255,255,255,0.04)] hover:border-[rgba(255,255,255,0.15)] transition-colors">
              <PencilSimple size={16} /> Edit workflow
            </button>
            <button onClick={openRunExperience} data-testid="run-test-header-btn"
              className="flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium text-[#7D8896] hover:text-[#B0B8C4] hover:bg-[rgba(255,255,255,0.04)] transition-colors">
              <Lightning size={16} /> Test
            </button>
            <button onClick={handleToggleStatus} data-testid="toggle-status-btn"
              className={`flex items-center gap-1.5 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive
                  ? 'text-[#7D8896] hover:text-[#B0B8C4] hover:bg-[rgba(255,255,255,0.04)]'
                  : 'bg-[#14B8A6] text-white hover:bg-[#0D9488] shadow-[0_2px_8px_rgba(20,184,166,0.25)]'
              }`}>
              {isActive ? <><Pause size={16} /> Pause</> : <><Lightning size={16} weight="fill" /> Activate</>}
            </button>
          </div>
        </div>
      </motion.div>

      {/* ═══════ READINESS WIDGET ═══════ */}
      <ReadinessWidget workerId={workerId} onConnect={(slug) => handleConnectApp(slug)} refreshKey={readinessRefreshKey} />

      {/* ═══════ CAPABILITY UNLOCK BANNER ═══════ */}
      {unlockedCapabilities && (
        <motion.div
          initial={{ opacity: 0, y: -8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          className="rounded-2xl border border-[rgba(0,229,192,0.2)] bg-[rgba(0,229,192,0.04)] p-5 mb-5"
          data-testid="capability-unlock-banner"
        >
          <div className="flex items-start justify-between mb-3">
            <p className="text-[13px] font-medium text-[#F0F4F8]">
              Your AI can now:
            </p>
            <button
              onClick={() => setUnlockedCapabilities(null)}
              className="text-[#5C6778] hover:text-[#7D8896] transition-colors p-0.5"
              data-testid="dismiss-unlock-btn"
            >
              <X size={14} />
            </button>
          </div>
          <div className="space-y-1.5">
            {unlockedCapabilities.capabilities.map((cap) => (
              <div key={cap.capability_id} className="flex items-center gap-2 text-[12px] text-[#B0B8C4]">
                <CheckCircle size={14} weight="fill" className="text-[#00E5C0] flex-shrink-0" />
                {cap.label}
              </div>
            ))}
          </div>
          <p className="text-[10px] text-[#5C6778] mt-3">
            via {unlockedCapabilities.provider}
          </p>
        </motion.div>
      )}

      {/* ═══════ FIRST WOW — Run a test enquiry ═══════ */}
      {!hasRuns && (
        <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.05 }}
          className="rounded-2xl border-2 border-[rgba(0,229,192,0.25)] bg-[rgba(0,229,192,0.04)] p-8 mb-5 text-center"
          data-testid="test-enquiry-banner">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[rgba(0,229,192,0.1)] text-[#00E5C0] text-xs font-semibold mb-4">
            <Sparkle size={14} weight="fill" /> {isActive ? 'Your worker is live' : 'See it in action'}
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">{runtimeMode === 'file' ? 'Upload a file to analyse' : runtimeMode === 'chat' ? 'Start a conversation' : 'Run this worker'}</h2>
          <p className="text-sm text-[#B0B8C4] mb-6 max-w-md mx-auto">
            {runtimeMode === 'file' 
              ? 'Upload a document, image, or media file to see how this worker analyses it.'
              : runtimeMode === 'chat'
              ? 'Have a conversation with this worker to see how it handles enquiries.'
              : 'Execute this worker to see it process your workflow in seconds.'}
          </p>
          <button onClick={openRunExperience} data-testid="test-enquiry-cta-btn"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#00E5C0] hover:bg-[#00CCB0] text-[#12161C] text-base font-bold transition-colors shadow-lg shadow-[rgba(0,229,192,0.15)]">
            <Play size={20} weight="fill" />
            {runtimeMode === 'file' ? 'Upload a file' : runtimeMode === 'chat' ? 'Start conversation' : 'Run worker'}
          </button>
        </motion.div>
      )}

      {/* ═══════ WORKFLOW — Live System Flow View ═══════ */}
      {(worker.steps || []).length > 0 && (
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.08 }}
          className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-6 mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)]" data-testid="worker-workflow">

          {/* ── Live Run Header ── */}
          {(() => {
            const isRunActive = liveRun && ['running', 'waiting', 'queued', 'retrying'].includes(liveRun.status);
            const isRunCompleted = liveRun && liveRun.status === 'completed';
            const isRunFailed = liveRun && liveRun.status === 'failed';
            const isRunWaiting = liveRun && liveRun.status === 'waiting';

            // Find the current step name from worker_runs
            const currentWr = liveRun?.worker_runs?.find(wr => wr.status === 'running');
            const waitingWr = liveRun?.worker_runs?.find(wr => wr.status === 'waiting');
            const currentStepLabel = currentWr
              ? (worker.steps?.[currentWr.sequence_index]?.label || currentWr.worker_name)
              : null;

            // Determine trigger description
            const triggerStep = (worker.steps || []).find(s => s.type === 'trigger');
            const triggerDesc = triggerStep?.app === 'Gmail'
              ? 'This runs automatically when a new email arrives'
              : triggerStep?.type === 'trigger'
              ? 'Runs when triggered'
              : 'Ready to run';

            return (
              <div className="mb-5" data-testid="live-run-header">
                {isRunActive ? (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#3B82F6] animate-pulse flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-[#F0F4F8]">
                        {currentStepLabel
                          ? `Currently: ${currentStepLabel}`
                          : liveRun.supervisor_summary || 'Running now'}
                      </p>
                      <p className="text-[11px] text-[#5C6778] mt-0.5">Running now</p>
                    </div>
                  </div>
                ) : isRunWaiting ? (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#F59E0B] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-[#F59E0B]">
                        {waitingWr?.waiting_reason === 'tool_connection'
                          ? `Waiting for ${waitingWr?.worker_name || 'connection'}`
                          : waitingWr?.waiting_reason === 'approval_required'
                          ? 'Waiting for approval'
                          : 'Action required to continue'}
                      </p>
                      <p className="text-[11px] text-[#5C6778] mt-0.5">{timeAgo(liveRun.created_at)}</p>
                    </div>
                  </div>
                ) : isRunFailed ? (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#EF4444] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-[#EF4444]">
                        {liveRun.error_summary || 'Last run failed'}
                      </p>
                      <p className="text-[11px] text-[#5C6778] mt-0.5">
                        {timeAgo(liveRun.completed_at || liveRun.created_at)}
                      </p>
                    </div>
                  </div>
                ) : isRunCompleted ? (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#00E5C0] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-[#B0B8C4]">
                        {liveRun.supervisor_summary || 'Completed successfully'}
                      </p>
                      <p className="text-[11px] text-[#5C6778] mt-0.5">
                        Last run {timeAgo(liveRun.completed_at || liveRun.created_at)}
                      </p>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-3">
                    <div className="w-2 h-2 rounded-full bg-[#00E5C0] flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-[13px] font-medium text-[#B0B8C4]">
                        {isActive ? 'Ready and running' : 'Ready to activate'}
                      </p>
                      <p className="text-[11px] text-[#5C6778] mt-0.5">{triggerDesc}</p>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] flex items-center gap-2">
              <ShareNetwork size={14} className="text-[#00E5C0]" /> Workflow
            </h2>
            <button onClick={() => navigate('/workers/new', { state: { editWorker: { id: workerId, name: worker.name, automation_type: worker.automation_type, steps: worker.steps, worker_config: worker.worker_config || wc } } })}
              data-testid="edit-workflow-inline-btn"
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] text-[#5C6778] hover:text-[#7D8896] transition-colors">
              <PencilSimple size={12} /> Edit
            </button>
          </div>

          {/* Inline connection message */}
          {!isActive && (
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-[rgba(249,115,22,0.05)] border border-[rgba(249,115,22,0.15)] mb-4" data-testid="connect-apps-hint">
              <PlugsConnected size={14} className="text-[#F97316] shrink-0" />
              <p className="text-xs text-[#F97316]">Connect your apps below to activate this worker</p>
            </div>
          )}

          <div className="space-y-0">
            {(() => {
              // Group apps by auth provider — Google apps share one OAuth
              const AUTH_PROVIDERS_MAP = {
                'Gmail': 'google', 'Google Calendar': 'google', 'Google Sheets': 'google',
                'Google Drive': 'google', 'Google Business Profile': 'google',
                'Outlook': 'microsoft',
              };
              const getProvider = (app) => AUTH_PROVIDERS_MAP[app] || app;
              const getProviderLabel = (app) => {
                const p = AUTH_PROVIDERS_MAP[app];
                if (p === 'google') return 'Connect Google';
                if (p === 'microsoft') return 'Connect Microsoft';
                return `Connect ${app}`;
              };
              const seenProviders = new Set();

              // Derive step groups from step types and content
              const getStepGroup = (step, index) => {
                if (step.type === 'trigger') return 'Trigger';
                if (step.type === 'ai') return 'Understand';
                const label = (step.label || '').toLowerCase();
                if (label.includes('check') || label.includes('availab') || label.includes('look up') || label.includes('search')) return 'Check';
                if (label.includes('send') || label.includes('reply') || label.includes('respond') || label.includes('email') || label.includes('sms')) return 'Respond';
                if (label.includes('create') || label.includes('update') || label.includes('save') || label.includes('log') || label.includes('contact')) return 'Update';
                return 'Act';
              };
              let lastGroup = null;

              // Map liveRun worker_runs to step indices for status
              const stepStatusMap = {};
              if (liveRun?.worker_runs) {
                liveRun.worker_runs.forEach(wr => {
                  stepStatusMap[wr.sequence_index] = wr.status;
                });
              }
              // Also derive a simpler per-step status from the overall run status + current index
              const getStepLiveStatus = (stepIndex) => {
                if (!liveRun) return null;
                const runStatus = liveRun.status;
                const currentIdx = liveRun.current_worker_index ?? 0;

                // Check explicit worker_run mapping first
                if (stepStatusMap[stepIndex]) return stepStatusMap[stepIndex];

                // Infer from position relative to current index
                if (runStatus === 'completed') return 'completed';
                if (runStatus === 'cancelled') return stepIndex < currentIdx ? 'completed' : null;
                if (stepIndex < currentIdx) return 'completed';
                if (stepIndex === currentIdx) {
                  if (runStatus === 'running') return 'running';
                  if (runStatus === 'waiting') return 'waiting';
                  if (runStatus === 'failed') return 'failed';
                  if (runStatus === 'retrying') return 'running';
                  return 'running';
                }
                return null; // future step, no status
              };

              // Resolve outcome-first label for each step
              const getOutcomeLabel = (step) => {
                // Try to map the step's capability to an outcome label
                if (step.capability) {
                  const capId = step.capability.startsWith('cap_') ? step.capability : `cap_${step.capability}`;
                  if (CAPABILITY_LABELS[capId]) return CAPABILITY_LABELS[capId];
                }
                // Infer capability from step type + action
                const label = (step.label || '').toLowerCase();
                if (step.type === 'trigger') return step.label; // Keep trigger labels as-is
                if (step.type === 'ai') return step.label; // Keep AI step labels as-is
                // Map common action patterns to outcomes
                if (label.includes('create') && label.includes('contact')) return CAPABILITY_LABELS.cap_create_contact || step.label;
                if (label.includes('update') && label.includes('contact')) return CAPABILITY_LABELS.cap_update_contact || step.label;
                if (label.includes('search') && label.includes('contact')) return CAPABILITY_LABELS.cap_search_contact || step.label;
                if (label.includes('send') && (label.includes('email') || label.includes('reply'))) return CAPABILITY_LABELS.cap_send_email || step.label;
                if (label.includes('send') && label.includes('sms')) return CAPABILITY_LABELS.cap_send_sms || step.label;
                if (label.includes('send') && (label.includes('slack') || label.includes('message') || label.includes('notification'))) return CAPABILITY_LABELS.cap_send_chat_message || step.label;
                if (label.includes('check') && label.includes('availab')) return CAPABILITY_LABELS.cap_check_availability || step.label;
                if (label.includes('create') && (label.includes('event') || label.includes('appointment'))) return CAPABILITY_LABELS.cap_create_event || step.label;
                if (label.includes('log') && label.includes('activity')) return CAPABILITY_LABELS.cap_log_activity || step.label;
                return step.label; // Fallback to original
              };

              // Step-aware connection check: trigger steps may need more than just OAuth
              const isConnectedForStep = (step, appSlug, resolvedApp) => {
                if (!appSlug) return false;
                const hasOAuth = connectedApps.has(appSlug);
                if (step.type === 'trigger') {
                  if (resolvedApp === 'Gmail') return hasOAuth && gmailConnected === true;
                  // Future: add calendar trigger checks here
                }
                return hasOAuth;
              };

              return (worker.steps || []).map((step, i) => {
                const style = STEP_STYLES[step.type] || STEP_STYLES.action;
                const resolvedApp = inferAppFromStep(step);
                const hasApp = !!resolvedApp;
                const provider = hasApp ? getProvider(resolvedApp) : null;
                const isFirstForProvider = hasApp && !seenProviders.has(provider);
                if (provider) seenProviders.add(provider);

                const appSlug = hasApp ? getSlug(resolvedApp) : null;
                const isConnected = isConnectedForStep(step, appSlug, resolvedApp);
                const isConnecting = appSlug && connectingApp === appSlug;

                // Step grouping
                const groupLabel = getStepGroup(step, i);
                const showGroupHeader = groupLabel !== lastGroup;
                lastGroup = groupLabel;

                // Live status for this step
                const liveStatus = getStepLiveStatus(i);

                // Step status indicator config
                const STATUS_ICON_MAP = {
                  completed: { Icon: CheckCircle, color: '#00E5C0', weight: 'fill' },
                  running: { Icon: Pulse, color: '#3B82F6', weight: 'bold' },
                  waiting: { Icon: HourglassMedium, color: '#F59E0B', weight: 'bold' },
                  failed: { Icon: X, color: '#EF4444', weight: 'bold' },
                  retrying: { Icon: ArrowsClockwise, color: '#F59E0B', weight: 'bold' },
                };
                const statusCfg = liveStatus ? STATUS_ICON_MAP[liveStatus] : null;

                // Step border override based on live status
                const liveStatusBorder = liveStatus === 'running'
                  ? 'border-[rgba(59,130,246,0.25)]'
                  : liveStatus === 'failed'
                  ? 'border-[rgba(239,68,68,0.25)]'
                  : liveStatus === 'waiting'
                  ? 'border-[rgba(245,158,11,0.25)]'
                  : liveStatus === 'completed'
                  ? 'border-[rgba(0,229,192,0.12)]'
                  : style.border;

                const liveBg = liveStatus === 'running'
                  ? 'bg-[rgba(59,130,246,0.05)]'
                  : liveStatus === 'failed'
                  ? 'bg-[rgba(239,68,68,0.04)]'
                  : liveStatus === 'waiting'
                  ? 'bg-[rgba(245,158,11,0.04)]'
                  : style.bg;

                return (
                  <div key={`step-${step.id || step.label || i}`}>
                    {/* Group header */}
                    {showGroupHeader && (
                      <p key={`group-${groupLabel}-${i}`} className="text-[9px] uppercase tracking-widest text-[#3A4150] font-semibold mt-3 mb-1.5 first:mt-0"
                        data-testid={`step-group-${groupLabel.toLowerCase()}`}>
                        {groupLabel}
                      </p>
                    )}
                    <div className={`flex items-center gap-3 p-3 rounded-xl ${liveBg} border ${liveStatusBorder} transition-all duration-200 hover:bg-[#2A3648] relative overflow-hidden group/step ${
                      liveStatus === 'running' ? 'ring-1 ring-[rgba(59,130,246,0.15)]' : ''
                    }`}
                      style={{ backgroundColor: !liveStatus ? '#243041' : undefined, boxShadow: '0 2px 6px rgba(0,0,0,0.2)' }}
                      data-testid={`workflow-step-${i}`}>
                      {/* Left accent line — inset pseudo via absolute div, no box-model impact */}
                      <div className={`absolute left-0 top-0 bottom-0 w-[2px] rounded-l-xl transition-all duration-200 ${
                        liveStatus === 'completed' ? 'bg-[rgba(74,222,128,0.6)] opacity-100'
                        : liveStatus === 'running' ? 'bg-[rgba(59,130,246,0.6)] opacity-100'
                        : 'bg-[rgba(20,184,166,0.4)] opacity-100 group-hover/step:bg-[rgba(20,184,166,0.6)]'
                      }`} />

                      {/* Step number or live status icon */}
                      {statusCfg ? (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          liveStatus === 'running' ? 'animate-pulse' : ''
                        }`}>
                          <statusCfg.Icon size={14} weight={statusCfg.weight} style={{ color: statusCfg.color }} />
                        </div>
                      ) : (
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${style.text} bg-[rgba(255,255,255,0.06)]`}>
                          {i + 1}
                        </div>
                      )}

                      {hasApp && (
                        <img src={getAppLogo(resolvedApp)} alt={resolvedApp} className="w-5 h-5 rounded object-contain shrink-0"
                          onError={(e) => { e.target.style.display = 'none'; }} />
                      )}
                      <span className={`text-sm font-medium flex-1 ${
                        liveStatus === 'completed' ? 'text-[#7D8896]'
                        : liveStatus === 'running' ? 'text-[#F0F4F8]'
                        : liveStatus === 'waiting' ? 'text-[#F59E0B]'
                        : liveStatus === 'failed' ? 'text-[#EF4444]'
                        : style.text
                      }`} data-testid={`step-outcome-label-${i}`}>
                        {getOutcomeLabel(step)}
                      </span>

                      {/* Waiting label */}
                      {liveStatus === 'waiting' && hasApp && !isConnected && (
                        <span className="text-[10px] text-[#F59E0B] flex-shrink-0">
                          Waiting for {resolvedApp}
                        </span>
                      )}
                      {liveStatus === 'waiting' && (!hasApp || isConnected) && (
                        <span className="text-[10px] text-[#F59E0B] flex-shrink-0">
                          Waiting...
                        </span>
                      )}

                      {/* Show Connect button ONLY when integration is missing */}
                      {hasApp && isFirstForProvider && !liveStatus && !isConnected ? (
                          <button
                            data-testid={`connect-${resolvedApp.toLowerCase().replace(/\s+/g, '-')}-btn`}
                            onClick={() => handleConnectApp(resolvedApp)}
                            disabled={!!connectingApp}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-[11px] font-semibold border border-[rgba(20,184,166,0.15)] bg-[rgba(20,184,166,0.04)] text-[#5EABA0] hover:bg-[rgba(20,184,166,0.08)] hover:border-[rgba(20,184,166,0.25)] transition-all shrink-0 disabled:opacity-50"
                          >
                            {isConnecting ? (
                              <CircleNotch size={12} className="animate-spin" />
                            ) : (
                              <PlugsConnected size={12} />
                            )}
                            {isConnecting ? 'Connecting...' : getProviderLabel(resolvedApp)}
                          </button>
                      ) : !hasApp && !liveStatus ? (
                        <span className="text-[9px] uppercase tracking-[0.08em] text-[#7D8896] shrink-0 px-2 py-1 rounded bg-[rgba(255,255,255,0.03)]">
                          {step.type === 'trigger' ? 'input' : 'ai'}
                        </span>
                      ) : null}
                    </div>
                    {/* Provider detail — progressive disclosure (P2) */}
                    {hasApp && isConnected && liveStatus === 'completed' && (
                      <div className="flex items-center gap-2 ml-9 mt-0.5 text-[10px] text-[#2F3640]" data-testid={`step-provider-detail-${i}`}>
                        <Info size={10} />
                        <span>Using: {step.app}</span>
                        <span className="text-[#252B33]">·</span>
                        <span className="text-[#252B33]">Auto-selected</span>
                      </div>
                    )}
                    {/* Connected state for idle steps (no live run) */}
                    {hasApp && isConnected && !liveStatus && (
                      <div className="flex items-center gap-2 ml-9 mt-0.5 text-[10px] text-[#2F3640]" data-testid={`step-connected-${i}`}>
                        <Info size={10} />
                        <span>Using {resolvedApp}</span>
                      </div>
                    )}
                    {i < (worker.steps || []).length - 1 && (
                      <div className="flex justify-center py-1">
                        <div className={`w-[2px] h-5 rounded-full transition-colors ${
                          liveStatus === 'completed' ? 'bg-[rgba(74,222,128,0.3)]' : 'bg-[rgba(20,184,166,0.15)]'
                        }`} />
                      </div>
                    )}
                  </div>
                );
              });
            })()}
          </div>

          {/* ── Inline Activity Summary ── */}
          {liveRun && (
            <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.08)] flex items-center justify-between" data-testid="workflow-activity-summary">
              <div className="flex items-center gap-3 text-[11px] text-[#5C6778]">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium border ${
                  liveRun.status === 'completed'
                    ? 'text-[#00E5C0] bg-[rgba(0,229,192,0.06)] border-[rgba(0,229,192,0.15)]'
                    : liveRun.status === 'failed'
                    ? 'text-[#EF4444] bg-[rgba(239,68,68,0.06)] border-[rgba(239,68,68,0.15)]'
                    : liveRun.status === 'running'
                    ? 'text-[#3B82F6] bg-[rgba(59,130,246,0.06)] border-[rgba(59,130,246,0.15)]'
                    : liveRun.status === 'waiting'
                    ? 'text-[#F59E0B] bg-[rgba(245,158,11,0.06)] border-[rgba(245,158,11,0.15)]'
                    : 'text-[#7D8896] bg-[rgba(255,255,255,0.03)] border-[rgba(255,255,255,0.08)]'
                }`}>
                  {liveRun.status === 'completed' ? 'Completed successfully'
                    : liveRun.status === 'failed' ? 'Failed'
                    : liveRun.status === 'running' ? 'Running'
                    : liveRun.status === 'waiting' ? 'Waiting for action'
                    : liveRun.status === 'cancelled' ? 'Cancelled'
                    : liveRun.status}
                </span>
                <span>{timeAgo(liveRun.completed_at || liveRun.created_at)}</span>
                {liveRun.duration_ms && <span>{liveRun.duration_ms < 1000 ? `${liveRun.duration_ms}ms` : `${Math.round(liveRun.duration_ms / 1000)}s`}</span>}
              </div>
              <button
                onClick={() => navigate(`/workers/activity/${liveRun.system_run_id}`)}
                data-testid="view-activity-link"
                className="text-[11px] text-[#00E5C0] hover:underline flex items-center gap-1"
              >
                View activity <ArrowRight size={10} />
              </button>
            </div>
          )}
          {!liveRun && !liveRunLoading && (
            <div className="mt-4 pt-4 border-t border-[rgba(255,255,255,0.08)]" data-testid="workflow-no-activity">
              <p className="text-[11px] text-[#3A4150]">No recent activity</p>
            </div>
          )}
        </motion.div>
      )}

      {/* ═══════ BEHAVIOR SETTINGS — hidden for file processing workers ═══════ */}
      {runtimeMode !== 'file' && (
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)]" data-testid="worker-behavior">
        <button onClick={() => setShowBehavior(!showBehavior)}
          className="w-full flex items-center justify-between p-5 text-left" data-testid="behavior-toggle">
          <div className="flex items-center gap-2">
            <Sliders size={14} className="text-[#00E5C0]" />
            <span className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em]">Behavior</span>
            <span className="text-[10px] text-[#5C6778]">Tone, style &amp; context</span>
          </div>
          <CaretDown size={14} className={`text-[#7D8896] transition-transform duration-200 ${showBehavior ? '' : '-rotate-90'}`} />
        </button>
        {showBehavior && (
          <div className="px-6 pb-6 border-t border-[rgba(255,255,255,0.08)] pt-5">
            <BehaviorSettings worker={worker} orgId={orgId} onUpdated={fetchWorker} />
          </div>
        )}
      </motion.div>
      )}

      {/* ═══════ ACTIVITY — Proof Engine ═══════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
        className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] p-6 mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)]" data-testid="worker-activity">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] flex items-center gap-2">
            <Clock size={14} /> Activity
          </h2>
          {executions.length > 0 && (
            <button onClick={() => navigate('/inbox')} className="text-[10px] text-[#00E5C0] hover:underline flex items-center gap-1">
              View All <ArrowRight size={10} />
            </button>
          )}
        </div>

        {executions.length === 0 ? (
          <div className="text-center py-6">
            <p className="text-sm text-[#7D8896]">
              {isActive ? 'No activity yet. Run this worker or enable the email trigger to get started.' : 'Activate this worker to get started.'}
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {executions.map((exec) => {
              const execId = exec.execution_id || exec.id;
              const isEmail = exec.trigger_source === 'email';
              const isFailed = exec.status === 'failed';
              const isExpanded = expandedExec === execId;
              const sender = isEmail
                ? (exec.input?.enquiry?.match(/Email from (.+?)\n/)?.[1] || exec.trigger_data?.from || 'Unknown')
                : null;
              const subject = isEmail
                ? (exec.input?.enquiry?.match(/Subject: (.+?)\n/)?.[1] || exec.trigger_data?.subject || null)
                : null;
              const rawOutput = exec.final_output;
              const outputText = typeof rawOutput === 'string'
                ? rawOutput
                : (rawOutput?.structured?.summary || rawOutput?.response || JSON.stringify(rawOutput || '', null, 2));
              const truncated = outputText.slice(0, 120);
              const hasMore = outputText.length > 120;
              const execLog = exec.execution_log || [];
              const isSequential = exec.execution_type === 'sequential';

              return (
                <div key={execId} data-testid={`activity-item-${execId}`}
                  className={`rounded-xl border transition-all duration-200 relative overflow-hidden hover:border-[rgba(255,255,255,0.1)] ${
                    isFailed ? 'bg-[#243041] border-[rgba(239,68,68,0.15)]' : 'bg-[#243041] border-[rgba(255,255,255,0.08)]'
                  }`}>
                  {/* Left status indicator */}
                  <div className={`absolute left-0 top-0 bottom-0 w-[3px] ${
                    isFailed ? 'bg-[#EF4444]' : 'bg-[#4ADE80]'
                  }`} />
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <img src={getWorkerIcon(worker.outcome_category, worker.name)} alt="" className="w-5 h-5 rounded object-cover shrink-0" />
                        {isEmail ? (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-[rgba(99,102,241,0.1)] text-[#818CF8] border border-[rgba(99,102,241,0.15)] whitespace-nowrap">
                            <EnvelopeSimple size={10} weight="fill" /> Email received
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-[rgba(255,255,255,0.04)] text-[#7D8896] border border-[rgba(255,255,255,0.08)] whitespace-nowrap">
                            <Lightning size={10} weight="fill" /> Manual run
                          </span>
                        )}
                        <span className={`text-[10px] px-2 py-0.5 rounded font-medium whitespace-nowrap ${
                          isFailed
                            ? 'bg-[rgba(239,68,68,0.1)] text-[#EF4444] border border-[rgba(239,68,68,0.2)]'
                            : 'bg-[rgba(0,229,192,0.08)] text-[#00E5C0] border border-[rgba(0,229,192,0.15)]'
                        }`}>
                          {isFailed ? 'Failed' : 'Handled'}
                        </span>
                      </div>
                      <span className="text-[10px] text-[#7D8896] whitespace-nowrap">{timeAgo(exec.created_at || exec.started_at)}</span>
                    </div>
                    {isEmail && sender && (
                      <p className="text-sm text-[#F0F4F8] mb-0.5">
                        From <span className="font-medium">{sender}</span>
                        {subject && <span className="text-[#7D8896]">: {subject}</span>}
                      </p>
                    )}

                    {/* Trust summary from execution log */}
                    {isSequential && execLog.length > 0 && (
                      <div className="flex items-center gap-1.5 mt-2 flex-wrap" data-testid={`exec-trust-badges-${execId}`}>
                        {execLog.filter(s => s.trust_label).map((s, si) => {
                          const isSuccess = s.execution_mode === 'auto' && s.tool_used;
                          const isFb = s.execution_mode === 'fallback';
                          const isFail = s.status === 'failed';
                          const color = isFail ? '#FF6B6B' : isSuccess ? '#00E5C0' : isFb ? '#F59E0B' : '#A5A8FC';
                          return (
                            <span key={`trust-${s.trust_label}-${si}`}
                              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[8px] font-semibold"
                              style={{ color, backgroundColor: `${color}10` }}
                              data-testid={`trust-badge-${si}`}>
                              {s.trust_label}
                            </span>
                          );
                        })}
                      </div>
                    )}

                    {outputText && (
                      <div className="mt-1">
                        <p className="text-xs text-[#B0B8C4] truncate">{truncated}{hasMore && !isExpanded ? '...' : ''}</p>
                        <div className="flex items-center gap-3 mt-1.5">
                          <button
                            onClick={() => toggleExecPreview(execId)}
                            data-testid={`expand-activity-${execId}`}
                            className="text-[11px] text-[#14B8A6] hover:text-[#2DD4BF] hover:underline flex items-center gap-1 font-semibold"
                          >
                            <CaretDown size={10} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                            {isExpanded ? 'Collapse' : 'View details'}
                          </button>
                          {(runtimeMode === 'file' || runtimeMode === 'process_export') && rawOutput && (
                            <button
                              onClick={() => setAnalysisResult({
                                output: rawOutput,
                                summary: exec.summary || '',
                                runId: execId,
                                fileName: exec.input?.file_name || exec.trigger_data?.file_name || '',
                              })}
                              data-testid={`view-result-${execId}`}
                              className="text-[11px] text-[#7D8896] hover:text-white flex items-center gap-1 font-medium"
                            >
                              <Eye size={10} /> Open full result
                            </button>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* ── Expanded: step timeline + output ── */}
                  {isExpanded && (
                    <div className="border-t border-[rgba(255,255,255,0.08)] px-4 py-3 bg-[rgba(0,229,192,0.02)]" data-testid={`activity-output-${execId}`}>
                      {/* Step timeline */}
                      {isSequential && execLog.length > 0 && (
                        <div className="mb-3 space-y-0" data-testid={`exec-timeline-${execId}`}>
                          <p className="text-[10px] font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em] mb-2">Step timeline</p>
                          {execLog.map((s, si) => {
                            const isLast = si === execLog.length - 1;
                            const color = s.status === 'failed' ? '#FF6B6B' : s.execution_mode === 'auto' && s.tool_used ? '#00E5C0' : s.execution_mode === 'fallback' ? '#F59E0B' : '#A5A8FC';
                            const ts = s.timestamp ? new Date(s.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' }) : '';
                            return (
                              <div key={`timeline-${s.step_label}-${si}`}>
                                <div className="flex items-center gap-2 px-2 py-1.5 rounded" data-testid={`detail-step-${si}`}>
                                  <div className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: color }} />
                                  <span className="text-[11px] text-[#C9D1D9] flex-1">{s.step_label}</span>
                                  <span className="text-[9px] font-medium" style={{ color }}>{s.trust_label || s.execution_mode}</span>
                                  {ts && <span className="text-[8px] text-[#5C6778]">{ts}</span>}
                                </div>
                                {!isLast && <div className="ml-[10px] w-px h-2 bg-[rgba(255,255,255,0.04)]" />}
                              </div>
                            );
                          })}
                        </div>
                      )}
                      {/* Render structured for file workers, raw for others */}
                      {(runtimeMode === 'file' || runtimeMode === 'process_export') && rawOutput ? (
                        <AnalysisResultView output={rawOutput} maxHeight={400} />
                      ) : (
                        <OutputRenderer output={rawOutput} maxHeight={400} />
                      )}
                      {/* Re-export button for file worker results */}
                      {(runtimeMode === 'file' || runtimeMode === 'process_export') && rawOutput && (
                        <div className="flex items-center gap-2 mt-3 pt-3 border-t border-[rgba(255,255,255,0.04)]">
                          <button
                            onClick={() => setAnalysisResult({
                              output: rawOutput,
                              summary: exec.summary || '',
                              runId: execId,
                              fileName: exec.input?.file_name || exec.trigger_data?.file_name || '',
                            })}
                            data-testid={`reexport-${execId}`}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[10px] font-medium text-[#B0B8C4] hover:text-white border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.12)] transition-all"
                          >
                            <DownloadSimple size={11} /> View & Export
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </motion.div>

      {/* ═══════ VERSION HISTORY ═══════ */}
      <WorkerVersionsPanel workerId={workerId} orgId={orgId} onReverted={fetchWorker} />


      {/* ═══════ WHERE THIS RUNS — Status & visibility layer ═══════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.22 }}
        className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)]" data-testid="worker-triggers-section">
        <button onClick={() => setShowTriggers(!showTriggers)}
          className="w-full flex items-center justify-between p-5 text-left" data-testid="triggers-toggle">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[rgba(0,229,192,0.08)] flex items-center justify-center">
              <Lightning size={16} className="text-[#00E5C0]" weight="duotone" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#F0F4F8]">Where this runs</p>
              <p className="text-[11px] text-[#7D8896]">Connected entry points for this worker</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {/* Active count badge — only count surfaces this worker actually supports */}
            {(() => {
              const supportsForm = _workerSupportsFormEntry(worker);
              const supportsCal = _workerSupportsCalendarEntry(worker);
              const activeCount = [
                supportsForm && formTrigger?.enabled,
                supportsCal && calendarTrigger?.enabled,
              ].filter(Boolean).length;
              return activeCount > 0 ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-[rgba(0,229,192,0.08)] text-[#00E5C0]">{activeCount} active</span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-[rgba(255,255,255,0.03)] text-[#5C6778]">None active</span>
              );
            })()}
            <CaretDown size={16} className={`text-[#7D8896] transition-transform duration-200 ${showTriggers ? 'rotate-180' : ''}`} />
          </div>
        </button>

        {showTriggers && (
          <div className="px-5 pb-5 space-y-3" data-testid="triggers-content">

            {/* ── Summary line ── */}
            {(() => {
              const sources = [];
              if (_workerSupportsFormEntry(worker) && formTrigger?.enabled) sources.push('website form submissions');
              if (_workerSupportsCalendarEntry(worker) && calendarTrigger?.enabled) sources.push('calendar events');
              if (_workerSupportsChat(worker)) sources.push('website chat interactions');
              return sources.length > 0 ? (
                <div className="px-4 py-2.5 rounded-lg bg-[rgba(0,229,192,0.03)] border border-[rgba(0,229,192,0.08)]" data-testid="triggers-summary">
                  <p className="text-[12px] text-[#B0B8C4]">
                    <span className="font-medium text-[#F0F4F8]">This worker runs when:</span>{' '}
                    {sources.join(', ')}
                  </p>
                </div>
              ) : (
                <div className="px-4 py-2.5 rounded-lg bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.06)]" data-testid="triggers-summary">
                  <p className="text-[12px] text-[#5C6778]">No entry points connected yet. Configure below to activate this worker.</p>
                </div>
              );
            })()}

            {/* ── Website Form ── */}
            {_workerSupportsFormEntry(worker) && (
            <div className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.015)] p-4" data-testid="form-trigger-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-[rgba(52,211,153,0.1)] flex items-center justify-center">
                    <FileText size={14} className="text-[#34D399]" weight="duotone" />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-[#F0F4F8]">Website Form</p>
                    <p className="text-[10px] text-[#7D8896]">Accepts lead and enquiry submissions</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {formTrigger ? (
                    <>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        formTrigger.enabled
                          ? 'bg-[rgba(0,229,192,0.08)] text-[#00E5C0]'
                          : 'bg-[rgba(255,255,255,0.04)] text-[#5C6778]'
                      }`} data-testid="form-trigger-status">
                        <span className={`w-1.5 h-1.5 rounded-full ${formTrigger.enabled ? 'bg-[#00E5C0]' : 'bg-[#5C6778]'}`} />
                        {formTrigger.enabled ? 'Active' : 'Paused'}
                      </span>
                      <button onClick={() => setShowFormConfig(p => !p)} className="px-2.5 py-1 rounded-lg text-[10px] font-medium text-[#7D8896] hover:text-[#F0F4F8] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)] transition-all" data-testid="form-trigger-manage">
                        {showFormConfig ? 'Close' : 'Manage'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={handleCreateFormTrigger}
                      disabled={savingFormTrigger}
                      className="px-3 py-1.5 rounded-lg text-[10px] font-medium text-[#7D8896] hover:text-[#34D399] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(52,211,153,0.25)] transition-all disabled:opacity-50"
                      data-testid="form-trigger-create"
                    >
                      {savingFormTrigger ? 'Setting up...' : 'Set up'}
                    </button>
                  )}
                </div>
              </div>

              {formTrigger && showFormConfig && (
                <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.04)] space-y-3">
                  {/* Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-[#7D8896]">Accept submissions</span>
                    <button onClick={() => handleUpdateFormTrigger({ enabled: !formTrigger.enabled })} disabled={savingFormTrigger}
                      className={`relative w-8 h-4.5 rounded-full transition-colors ${formTrigger.enabled ? 'bg-[#00E5C0]' : 'bg-[rgba(255,255,255,0.12)]'}`}
                      style={{ height: 18 }} data-testid="form-trigger-toggle">
                      <span className={`absolute top-0.5 w-3.5 h-3.5 rounded-full bg-white transition-transform shadow-sm ${formTrigger.enabled ? 'left-[18px]' : 'left-0.5'}`} style={{ width: 14, height: 14 }} />
                    </button>
                  </div>
                  {/* Endpoint */}
                  <div className="bg-[rgba(0,0,0,0.2)] rounded-lg p-3">
                    <p className="text-[10px] text-[#7D8896] mb-1">Endpoint URL</p>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-[11px] text-[#34D399] bg-[rgba(0,0,0,0.3)] px-2.5 py-1.5 rounded font-mono truncate" data-testid="form-trigger-url">
                        {`${window.location.origin}/api/service-os/form-trigger/${formTrigger.form_trigger_id}`}
                      </code>
                      <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/api/service-os/form-trigger/${formTrigger.form_trigger_id}`); toast.success('URL copied'); }}
                        className="p-1.5 rounded-lg hover:bg-[rgba(255,255,255,0.05)] transition-colors" data-testid="form-trigger-copy">
                        <Copy size={14} className="text-[#7D8896]" />
                      </button>
                    </div>
                  </div>
                  {/* Field Mappings */}
                  <div>
                    <p className="text-[10px] font-medium text-[#B0B8C4] mb-2">Field Mappings</p>
                    <div className="space-y-1.5">
                      {formMappingDraft.map((m, i) => (
                        <div key={`mapping-${m.source_key || ''}-${m.target_field || ''}-${i}`} className="flex items-center gap-2" data-testid={`field-mapping-${i}`}>
                          <input value={m.source_key || ''} onChange={e => { const d = [...formMappingDraft]; d[i] = { ...d[i], source_key: e.target.value }; setFormMappingDraft(d); }}
                            placeholder="Form field" className="flex-1 text-[11px] bg-[rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.08)] rounded-lg px-2.5 py-1.5 text-[#F0F4F8] placeholder-[#5C6778] focus:border-[rgba(52,211,153,0.3)] outline-none" data-testid={`mapping-source-${i}`} />
                          <span className="text-[10px] text-[#5C6778]">&rarr;</span>
                          <select value={m.target_field || ''} onChange={e => { const d = [...formMappingDraft]; d[i] = { ...d[i], target_field: e.target.value }; setFormMappingDraft(d); }}
                            className="flex-1 text-[11px] bg-[rgba(0,0,0,0.3)] border border-[rgba(255,255,255,0.08)] rounded-lg px-2.5 py-1.5 text-[#F0F4F8] focus:border-[rgba(52,211,153,0.3)] outline-none" data-testid={`mapping-target-${i}`}>
                            <option value="">Select field</option>
                            <option value="customer_name">Customer Name</option>
                            <option value="customer_email">Customer Email</option>
                            <option value="customer_phone">Customer Phone</option>
                            <option value="enquiry">Enquiry / Message</option>
                            <option value="metadata">Metadata</option>
                          </select>
                          <button onClick={() => { const d = [...formMappingDraft]; d.splice(i, 1); setFormMappingDraft(d); }}
                            className="p-1 rounded hover:bg-[rgba(255,255,255,0.05)] text-[#7D8896] hover:text-[#EF4444]" data-testid={`mapping-remove-${i}`}>
                            <Trash size={12} />
                          </button>
                        </div>
                      ))}
                      <button onClick={() => setFormMappingDraft([...formMappingDraft, { source_key: '', target_field: '', default_value: '' }])}
                        className="inline-flex items-center gap-1 text-[10px] text-[#7D8896] hover:text-[#34D399] transition-colors" data-testid="add-field-mapping">
                        <Plus size={10} /> Add field
                      </button>
                    </div>
                  </div>
                  {/* Save / Stats / Delete */}
                  <div className="flex items-center justify-between pt-2 border-t border-[rgba(255,255,255,0.04)]">
                    <button onClick={() => handleUpdateFormTrigger({ field_mappings: formMappingDraft })} disabled={savingFormTrigger}
                      className="px-3 py-1.5 rounded-lg bg-[rgba(52,211,153,0.1)] text-[#34D399] text-[10px] font-medium hover:bg-[rgba(52,211,153,0.15)] transition-colors disabled:opacity-50" data-testid="form-trigger-save">
                      {savingFormTrigger ? 'Saving...' : 'Save'}
                    </button>
                    <div className="flex items-center gap-3 text-[10px] text-[#5C6778]">
                      <span>{formTrigger.trigger_count || 0} submissions</span>
                      <button onClick={handleDeleteFormTrigger} className="text-[#7D8896] hover:text-[#EF4444] transition-colors" data-testid="form-trigger-delete"><Trash size={12} /></button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* ── Calendar Events ── */}
            {_workerSupportsCalendarEntry(worker) && (
            <div className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.015)] p-4" data-testid="calendar-trigger-card">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-[rgba(251,146,60,0.1)] flex items-center justify-center">
                    <CalendarBlank size={14} className="text-[#FB923C]" weight="duotone" />
                  </div>
                  <div>
                    <p className="text-[13px] font-semibold text-[#F0F4F8]">Calendar Events</p>
                    <p className="text-[10px] text-[#7D8896]">Reacts to new, changed, or cancelled events</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {calendarTrigger ? (
                    <>
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                        calendarTrigger.enabled
                          ? 'bg-[rgba(0,229,192,0.08)] text-[#00E5C0]'
                          : 'bg-[rgba(255,255,255,0.04)] text-[#5C6778]'
                      }`} data-testid="cal-trigger-status">
                        <span className={`w-1.5 h-1.5 rounded-full ${calendarTrigger.enabled ? 'bg-[#00E5C0]' : 'bg-[#5C6778]'}`} />
                        {calendarTrigger.enabled ? 'Active' : 'Paused'}
                      </span>
                      <button onClick={() => setShowCalConfig(p => !p)} className="px-2.5 py-1 rounded-lg text-[10px] font-medium text-[#7D8896] hover:text-[#F0F4F8] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)] transition-all" data-testid="cal-trigger-manage">
                        {showCalConfig ? 'Close' : 'Manage'}
                      </button>
                    </>
                  ) : !calendarConnected ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-medium text-[#FB923C] border border-[rgba(251,146,60,0.15)]">
                      <span className="w-1.5 h-1.5 rounded-full bg-[#FB923C]" /> Not connected
                    </span>
                  ) : (
                    <button onClick={handleCreateCalendarTrigger} disabled={savingCalTrigger}
                      className="px-3 py-1.5 rounded-lg text-[10px] font-medium text-[#7D8896] hover:text-[#FB923C] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(251,146,60,0.25)] transition-all disabled:opacity-50" data-testid="cal-trigger-create">
                      {savingCalTrigger ? 'Setting up...' : 'Connect calendar'}
                    </button>
                  )}
                </div>
              </div>

              {!calendarConnected && !calendarTrigger && (
                <p className="text-[10px] text-[#5C6778] mt-2 pl-9">Connect Google Calendar in integrations to activate this entry point.</p>
              )}

              {calendarTrigger && showCalConfig && (
                <div className="mt-3 pt-3 border-t border-[rgba(255,255,255,0.04)] space-y-3">
                  {/* Toggle */}
                  <div className="flex items-center justify-between">
                    <span className="text-[10px] text-[#7D8896]">Listen for events</span>
                    <button onClick={() => handleUpdateCalendarTrigger({ enabled: !calendarTrigger.enabled })} disabled={savingCalTrigger}
                      className={`relative w-8 rounded-full transition-colors ${calendarTrigger.enabled ? 'bg-[#00E5C0]' : 'bg-[rgba(255,255,255,0.12)]'}`}
                      style={{ height: 18 }} data-testid="cal-trigger-toggle">
                      <span className={`absolute top-0.5 rounded-full bg-white transition-transform shadow-sm ${calendarTrigger.enabled ? 'left-[18px]' : 'left-0.5'}`} style={{ width: 14, height: 14 }} />
                    </button>
                  </div>
                  {/* Connection */}
                  <div className="flex items-center gap-2">
                    <span className={`w-1.5 h-1.5 rounded-full ${calendarConnected ? 'bg-[#00E5C0]' : 'bg-[#EF4444]'}`} />
                    <span className="text-[10px] text-[#7D8896]">{calendarConnected ? 'Google Calendar connected' : 'Calendar not connected'}</span>
                  </div>
                  {/* Event types */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {['created', 'updated', 'cancelled'].map(et => {
                      const active = (calendarTrigger.event_types || []).includes(et);
                      return (
                        <button key={et} onClick={() => { const c = new Set(calendarTrigger.event_types || []); if (active) c.delete(et); else c.add(et); if (c.size > 0) handleUpdateCalendarTrigger({ event_types: [...c] }); }}
                          className={`px-2.5 py-1 rounded-lg text-[10px] font-medium transition-colors border ${active ? 'bg-[rgba(251,146,60,0.1)] text-[#FB923C] border-[rgba(251,146,60,0.2)]' : 'bg-[rgba(255,255,255,0.02)] text-[#5C6778] border-[rgba(255,255,255,0.08)] hover:text-[#7D8896]'}`}
                          data-testid={`cal-event-type-${et}`}>
                          {et === 'created' ? 'New' : et === 'updated' ? 'Changed' : 'Cancelled'}
                        </button>
                      );
                    })}
                  </div>
                  {/* Stats + Delete */}
                  <div className="flex items-center justify-between pt-2 border-t border-[rgba(255,255,255,0.04)]">
                    <div className="flex items-center gap-3 text-[10px] text-[#5C6778]">
                      <span>{calendarTrigger.trigger_count || 0} events</span>
                      {calendarTrigger.last_polled_at && <span>Last check: {timeAgo(calendarTrigger.last_polled_at)}</span>}
                    </div>
                    <button onClick={handleDeleteCalendarTrigger} className="text-[#7D8896] hover:text-[#EF4444] transition-colors" data-testid="cal-trigger-delete"><Trash size={12} /></button>
                  </div>
                </div>
              )}
            </div>
            )}

            {/* ── Website Chat ── */}
            {_workerSupportsChat(worker) && (
              <div className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.015)] p-4" data-testid="embed-trigger-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2.5">
                    <div className="w-7 h-7 rounded-lg bg-[rgba(99,102,241,0.1)] flex items-center justify-center">
                      <Globe size={14} className="text-[#818CF8]" weight="duotone" />
                    </div>
                    <div>
                      <p className="text-[13px] font-semibold text-[#F0F4F8]">Website Chat</p>
                      <p className="text-[10px] text-[#7D8896]">Chat widget, inline form, or shareable link</p>
                    </div>
                  </div>
                  <button onClick={() => setShowDeployModal(true)}
                    className="px-2.5 py-1 rounded-lg text-[10px] font-medium text-[#7D8896] hover:text-[#818CF8] border border-[rgba(255,255,255,0.08)] hover:border-[rgba(99,102,241,0.25)] transition-all"
                    data-testid="open-embed-btn">
                    Manage
                  </button>
                </div>
              </div>
            )}

            {/* ── Empty state when no entry-points are compatible ── */}
            {!_workerSupportsFormEntry(worker) && !_workerSupportsCalendarEntry(worker) && !_workerSupportsChat(worker) && (
              <div className="rounded-xl border border-dashed border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.015)] p-4 text-center" data-testid="entry-points-empty">
                <p className="text-[12px] text-[#B0B8C4] font-medium">This worker runs from its built-in trigger only.</p>
                <p className="text-[10px] text-[#7D8896] mt-1">No additional entry points apply to this worker type.</p>
              </div>
            )}

          </div>
        )}
      </motion.div>

      {/* ═══════ ADVANCED — Available to all users ═══════ */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
          className="rounded-2xl border border-[rgba(255,255,255,0.08)] bg-[#1E2836] mb-5 shadow-[0_1px_3px_rgba(0,0,0,0.14),0_6px_20px_rgba(0,0,0,0.10)]" data-testid="worker-advanced">
          <button onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full flex items-center justify-between p-5 text-left" data-testid="advanced-toggle">
            <div className="flex items-center gap-2">
              <GearSix size={14} className="text-[#7D8896]" />
              <span className="text-xs font-semibold text-[rgba(255,255,255,0.75)] uppercase tracking-[0.08em]">Advanced</span>
            </div>
            <CaretDown size={14} className={`text-[#7D8896] transition-transform duration-200 ${showAdvanced ? '' : '-rotate-90'}`} />
          </button>
          {showAdvanced && (
            <div className="px-6 pb-6 space-y-4 border-t border-[rgba(255,255,255,0.08)] pt-5">
              {/* Metadata */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
                  <p className="text-[10px] text-[#7D8896] mb-1">Type</p>
                  <p className="text-white text-sm font-medium capitalize">{(worker.automation_type || 'custom').replace(/_/g, ' ')}</p>
                </div>
                <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
                  <p className="text-[10px] text-[#7D8896] mb-1">Steps</p>
                  <p className="text-white text-sm font-medium">{(worker.steps || []).length}</p>
                </div>
                <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
                  <p className="text-[10px] text-[#7D8896] mb-1">Runs</p>
                  <p className="text-white text-sm font-medium">{worker.run_count || 0}</p>
                </div>
                <div className="p-3 rounded-xl bg-[rgba(255,255,255,0.02)] border border-[rgba(255,255,255,0.08)]">
                  <p className="text-[10px] text-[#7D8896] mb-1">Created</p>
                  <p className="text-white text-sm font-medium">{new Date(worker.created_at).toLocaleDateString()}</p>
                </div>
              </div>
              {worker.outcome_id && (
                <div className="text-xs text-[#5C6778]">Outcome: {worker.outcome_id}</div>
              )}
            </div>
          )}
        </motion.div>

      {/* ═══════ FILE ANALYSIS MODAL — Premium Design ═══════ */}
      {showTestInput && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50" onClick={() => { if (!executing) { setShowTestInput(false); setTestFile(null); setRefFile(null); setDragging(false); }}} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-[#131A26] border border-[rgba(255,255,255,0.08)] rounded-2xl w-full max-w-xl shadow-[0_32px_80px_rgba(0,0,0,0.6),0_0_0_1px_rgba(255,255,255,0.03)] max-h-[90vh] flex flex-col overflow-hidden" data-testid="test-input-modal">

              {/* ── Header ── */}
              <div className="relative px-6 pt-6 pb-5 border-b border-[rgba(255,255,255,0.06)]">
                <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#00E5C0] to-transparent opacity-50" />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <img
                      src={getWorkerIcon(worker?.outcome_category, worker?.name)}
                      alt=""
                      className="w-10 h-10 rounded-xl object-cover border border-[rgba(255,255,255,0.08)] shrink-0"
                      data-testid="modal-worker-icon"
                    />
                    <div>
                      <h3 className="text-[15px] font-semibold text-[#F0F4F8] leading-tight" data-testid="modal-worker-name">{worker?.name || 'Run Worker'}</h3>
                      <p className="text-[12px] text-[#7D8896] mt-0.5">
                        {isFileWorker ? 'Upload your file and run this worker' : 'Test this worker with an enquiry'}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => { if (!executing) { setShowTestInput(false); setTestFile(null); setRefFile(null); setDragging(false); }}} className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[rgba(255,255,255,0.06)] text-[#5C6778] hover:text-[#B0B8C4] transition-all" data-testid="close-test-modal-btn">
                    <X size={15} />
                  </button>
                </div>
              </div>

              {/* ── Scrollable content ── */}
              <div className="px-6 py-6 overflow-y-auto flex-1 space-y-6">
                {isFileWorker ? (
                  <>
                    {/* ─── 1. FILE UPLOAD — Primary focus ─── */}
                    <div>
                      <label className="text-[11px] font-semibold text-[#B0B8C4] uppercase tracking-[0.06em] mb-2.5 block">Upload file to analyse</label>
                      <div
                        data-testid="file-drop-zone"
                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={(e) => { e.preventDefault(); setDragging(false); const f = e.dataTransfer.files[0]; if (f) setTestFile(f); }}
                        onClick={() => document.getElementById('worker-file-input')?.click()}
                        className={`relative flex flex-col items-center justify-center py-10 px-6 border-2 border-dashed rounded-2xl cursor-pointer transition-all duration-300 group ${
                          dragging ? 'border-[#00E5C0] bg-[rgba(0,229,192,0.06)] shadow-[0_0_30px_rgba(0,229,192,0.1)]'
                            : testFile ? 'border-[rgba(0,229,192,0.35)] bg-[rgba(0,229,192,0.04)]'
                            : 'border-[rgba(255,255,255,0.12)] hover:border-[rgba(0,229,192,0.25)] bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(0,229,192,0.02)]'
                        }`}
                      >
                        <input id="worker-file-input" type="file" className="hidden"
                          accept={(() => {
                            const extMap = { pdf: '.pdf', doc: '.doc,.docx,.txt', image: '.png,.jpg,.jpeg,.webp,.heic', audio: '.mp3,.wav,.m4a,.ogg', video: '.mp4,.mov,.webm', sheet: '.xlsx,.xls,.csv' };
                            const accepted = wc.acceptedTypes || [];
                            if (accepted.length === 0) return Object.values(extMap).join(',');
                            return accepted.map(t => extMap[t] || '').filter(Boolean).join(',');
                          })()}
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) setTestFile(f); }} />
                        {testFile ? (
                          <div className="flex items-center gap-4 w-full">
                            <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[rgba(0,229,192,0.18)] to-[rgba(0,229,192,0.04)] border border-[rgba(0,229,192,0.25)] flex items-center justify-center shrink-0">
                              <CheckCircle size={26} weight="fill" className="text-[#00E5C0]" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="text-[14px] font-medium text-[#F0F4F8] truncate">{testFile.name}</p>
                              <p className="text-[12px] text-[#7D8896] mt-1">{(testFile.size / 1024).toFixed(1)} KB &middot; Ready to analyse</p>
                            </div>
                            <button onClick={(e) => { e.stopPropagation(); setTestFile(null); }}
                              data-testid="remove-test-file-btn"
                              className="w-8 h-8 rounded-lg flex items-center justify-center hover:bg-[rgba(239,68,68,0.1)] text-[#5C6778] hover:text-red-400 transition-all shrink-0">
                              <Trash size={14} />
                            </button>
                          </div>
                        ) : (
                          <>
                            <div className="w-14 h-14 rounded-2xl bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.08)] group-hover:border-[rgba(0,229,192,0.2)] group-hover:bg-[rgba(0,229,192,0.05)] flex items-center justify-center mb-4 transition-all duration-300">
                              <Upload size={24} className="text-[#5C6778] group-hover:text-[#00E5C0] transition-colors duration-300" />
                            </div>
                            <p className="text-[14px] font-medium text-[#B0B8C4] group-hover:text-white transition-colors">Drop a file here or click to browse</p>
                            <p className="text-[12px] text-[#5C6778] mt-1.5">Supported formats</p>
                            <div className="flex items-center gap-2 mt-2 flex-wrap justify-center">
                              {(() => {
                                const allTypes = [
                                  {id: 'pdf', icon: FilePdf, label:'PDF'},
                                  {id: 'doc', icon: FileDoc, label:'Word'},
                                  {id: 'image', icon: FileImage, label:'Images'},
                                  {id: 'audio', icon: FileAudio, label:'Audio'},
                                  {id: 'video', icon: FileVideo, label:'Video'},
                                  {id: 'sheet', icon: FileXls, label:'Sheets'},
                                ];
                                const accepted = wc.acceptedTypes || [];
                                const filtered = accepted.length > 0 ? allTypes.filter(t => accepted.includes(t.id)) : allTypes;
                                return filtered.map(({icon: Ic, label}) => (
                                  <span key={label} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-medium text-[#7D8896] bg-[rgba(255,255,255,0.03)] border border-[rgba(255,255,255,0.06)]">
                                    <Ic size={12} weight="duotone" className="text-[#5C6778]" /> {label}
                                  </span>
                                ));
                              })()}
                            </div>
                          </>
                        )}
                      </div>
                    </div>

                    {/* ─── 2. INSTRUCTIONS — Secondary ─── */}
                    <div>
                      <label className="text-[11px] font-semibold text-[#B0B8C4] uppercase tracking-[0.06em] mb-1 block">
                        What should the worker focus on? <span className="text-[#5C6778] font-normal normal-case tracking-normal">(optional)</span>
                      </label>
                      <p className="text-[11px] text-[#5C6778] mb-2.5">Guide the analysis with specific instructions or areas of interest</p>
                      <textarea
                        value={testEnquiry}
                        onChange={(e) => setTestEnquiry(e.target.value)}
                        rows={2}
                        placeholder="e.g. Focus on the financial summary section..."
                        data-testid="test-instructions-input"
                        className="w-full px-4 py-3 bg-[#1A2232] border border-[rgba(255,255,255,0.1)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#4A5568] focus:outline-none focus:border-[rgba(0,229,192,0.35)] focus:bg-[#1C2536] transition-all resize-none"
                      />
                    </div>

                    {/* ─── 3. REFERENCE MATERIAL — Optional card ─── */}
                    {(showRefText || showRefFile) && (
                      <div className="rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] p-5">
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2.5">
                            <ClipboardText size={14} className="text-[#7D8896]" />
                            <label className="text-[11px] font-semibold text-[#B0B8C4] uppercase tracking-[0.06em]">Reference material</label>
                            {savedRefText || savedRefFileName ? (
                              <span className="text-[9px] px-2 py-0.5 rounded-full bg-[rgba(0,229,192,0.08)] text-[#00E5C0] font-semibold border border-[rgba(0,229,192,0.15)]">Saved</span>
                            ) : refRequired ? (
                              <span className="text-[9px] px-2 py-0.5 rounded-full bg-[rgba(239,68,68,0.08)] text-red-400 font-semibold border border-[rgba(239,68,68,0.15)]">Required</span>
                            ) : (
                              <span className="text-[9px] px-2 py-0.5 rounded-full bg-[rgba(255,255,255,0.04)] text-[#5C6778] font-medium border border-[rgba(255,255,255,0.06)]">Optional</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {(refText.trim() && refText !== savedRefText) && (
                              <button
                                onClick={() => { saveReferenceToWorker(refText, ''); toast.success('Reference saved for future runs'); }}
                                data-testid="save-ref-btn"
                                className="text-[10px] text-[#00E5C0] hover:text-white font-semibold flex items-center gap-1 transition-colors"
                              >
                                <FloppyDisk size={10} /> Save
                              </button>
                            )}
                            {savedRefText && (
                              <button
                                onClick={() => { saveReferenceToWorker('', ''); setRefText(''); toast.success('Reference removed'); }}
                                data-testid="clear-ref-btn"
                                className="text-[10px] text-[#5C6778] hover:text-red-400 font-medium flex items-center gap-1 transition-colors"
                              >
                                <Trash size={10} /> Clear
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-[11px] text-[#5C6778] mb-3">
                          {savedRefText ? 'Using saved rubric/criteria. Edit below or clear to start fresh.' : 'Use a rubric, checklist, or criteria to guide the evaluation'}
                        </p>

                        {showRefText && (
                          <textarea
                            value={refText}
                            onChange={(e) => setRefText(e.target.value)}
                            rows={3}
                            placeholder="Paste rubric, marking guide, or evaluation criteria..."
                            data-testid="ref-text-input"
                            className="w-full px-4 py-3 bg-[#1A2232] border border-[rgba(255,255,255,0.1)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#4A5568] focus:outline-none focus:border-[rgba(245,158,11,0.35)] transition-all resize-none"
                          />
                        )}

                        {showRefFile && showRefText && (
                          <div className="flex items-center gap-3 my-3">
                            <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
                            <span className="text-[10px] font-semibold text-[#5C6778] uppercase tracking-widest">or</span>
                            <div className="flex-1 h-px bg-[rgba(255,255,255,0.06)]" />
                          </div>
                        )}

                        {showRefFile && (
                          <div>
                            {refFile ? (
                              <div className="flex items-center gap-3 px-4 py-3 bg-[rgba(245,158,11,0.04)] border border-[rgba(245,158,11,0.15)] rounded-xl">
                                <div className="w-8 h-8 rounded-lg bg-[rgba(245,158,11,0.12)] flex items-center justify-center shrink-0">
                                  <FileText size={15} className="text-[#F59E0B]" />
                                </div>
                                <div className="flex-1 min-w-0">
                                  <p className="text-[12px] font-medium text-[#F0F4F8] truncate">{refFile.name}</p>
                                  <p className="text-[11px] text-[#7D8896]">{(refFile.size / 1024).toFixed(1)} KB</p>
                                </div>
                                <button onClick={() => setRefFile(null)} className="text-[#5C6778] hover:text-red-400 transition-colors" data-testid="remove-ref-file">
                                  <X size={13} />
                                </button>
                              </div>
                            ) : (
                              <button
                                onClick={() => document.getElementById('worker-ref-file-input')?.click()}
                                data-testid="ref-file-upload-btn"
                                className="w-full flex items-center justify-center gap-2.5 px-4 py-3 border border-dashed border-[rgba(255,255,255,0.1)] rounded-xl text-[12px] font-medium text-[#7D8896] hover:border-[rgba(245,158,11,0.25)] hover:text-[#F59E0B] hover:bg-[rgba(245,158,11,0.03)] transition-all"
                              >
                                <Upload size={14} />
                                Upload reference file (PDF, DOCX, TXT)
                              </button>
                            )}
                            <input id="worker-ref-file-input" type="file" className="hidden" accept=".pdf,.doc,.docx,.txt,.csv,.xlsx"
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) setRefFile(f); }} />
                          </div>
                        )}
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <p className="text-[13px] text-[#7D8896] mb-3">Paste a real customer enquiry to see how this worker handles it.</p>
                    <textarea
                      value={testEnquiry}
                      onChange={(e) => setTestEnquiry(e.target.value)}
                      autoFocus
                      rows={5}
                      placeholder="e.g. Hi, I need a plumber to fix a leaking tap at my house in Melbourne. Are you available this week? Thanks, Sarah"
                      data-testid="test-enquiry-input"
                      className="w-full px-4 py-3 bg-[#1A2232] border border-[rgba(255,255,255,0.1)] rounded-xl text-sm text-[#F0F4F8] placeholder-[#4A5568] focus:outline-none focus:border-[rgba(0,229,192,0.35)] transition-all resize-none"
                    />
                  </>
                )}
              </div>

              {/* ── Footer ── */}
              <div className="px-6 py-4 border-t border-[rgba(255,255,255,0.06)] bg-[#0F151F] flex items-center justify-between">
                <div className="text-[11px] text-[#5C6778]">
                  {testFile && <span>Credits deducted based on file size</span>}
                </div>
                <div className="flex items-center gap-3">
                  <button onClick={() => { setShowTestInput(false); setTestFile(null); setRefFile(null); }}
                    disabled={executing}
                    data-testid="cancel-test-btn"
                    className="px-4 py-2.5 rounded-xl text-[13px] font-medium text-[#7D8896] hover:text-[#B0B8C4] hover:bg-[rgba(255,255,255,0.04)] transition-all disabled:opacity-40">
                    Cancel
                  </button>
                  <button onClick={() => handleRun(testEnquiry)}
                    disabled={executing || (isFileWorker ? (!testFile || (refRequired && !refText.trim() && !refFile)) : !testEnquiry.trim())}
                    data-testid="test-submit-btn"
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-[#00E5C0] hover:bg-[#00D4B1] text-[#0A1018] text-[13px] font-bold transition-all disabled:opacity-25 shadow-[0_2px_12px_rgba(0,229,192,0.2)] hover:shadow-[0_4px_20px_rgba(0,229,192,0.3)]">
                    {executing ? (
                      <>
                        <CircleNotch size={15} className="animate-spin" />
                        <span>Analysing...</span>
                      </>
                    ) : (
                      <>
                        <Play size={15} weight="fill" />
                        <span>Run Worker</span>
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════ ANALYSIS RESULTS MODAL ═══════ */}
      {analysisResult && (
        <>
          <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-50" onClick={() => setAnalysisResult(null)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-[#151D2B] border border-[rgba(255,255,255,0.06)] rounded-2xl w-full max-w-2xl shadow-[0_24px_80px_rgba(0,0,0,0.5)] max-h-[90vh] flex flex-col overflow-hidden" data-testid="analysis-result-modal">
              {/* Header */}
              <div className="relative px-6 pt-5 pb-4 shrink-0">
                <div className="absolute inset-x-0 top-0 h-[2px] bg-gradient-to-r from-transparent via-[#00E5C0] to-transparent opacity-60" />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[rgba(0,229,192,0.15)] to-[rgba(0,229,192,0.05)] border border-[rgba(0,229,192,0.2)] flex items-center justify-center">
                      <CheckCircle size={18} weight="fill" className="text-[#00E5C0]" />
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-white">
                        {runtimeMode === 'process_export' ? 'Your report is ready' : runtimeMode === 'process' ? 'Run complete' : 'Analysis complete'}
                      </h3>
                      <p className="text-[11px] text-[#5C6778] mt-0.5">
                        {analysisResult.fileName && <span>{analysisResult.fileName} &middot; </span>}
                        {analysisResult.summary || 'Results ready'}
                      </p>
                    </div>
                  </div>
                  <button onClick={() => setAnalysisResult(null)} className="w-7 h-7 rounded-lg flex items-center justify-center hover:bg-[rgba(255,255,255,0.05)] text-[#5C6778] hover:text-white transition-all" data-testid="close-result-x">
                    <X size={14} />
                  </button>
                </div>
              </div>

              {/* Export cards — prominent for process_export workers */}
              {runtimeMode === 'process_export' && (
                <div className="px-6 pb-3" data-testid="export-cards">
                  <p className="text-[11px] font-semibold text-[#7D8896] uppercase tracking-wider mb-2.5">Download your report</p>
                  <div className="grid grid-cols-3 gap-2.5">
                    {[
                      { id: 'pdf',  label: 'PDF',  desc: 'Best for sharing',  icon: FilePdf,  color: '#EF4444' },
                      { id: 'docx', label: 'Word', desc: 'Editable document', icon: FileDoc,  color: '#3B82F6' },
                      { id: 'txt',  label: 'Text', desc: 'Plain text',        icon: FileText, color: '#7D8896' },
                    ].map(opt => (
                      <button
                        key={opt.id}
                        data-testid={`export-card-${opt.id}`}
                        onClick={async () => {
                          const out = analysisResult.output;
                          const structured = typeof out === 'object' ? out?.structured : null;
                          const rawText = typeof out === 'string' ? out : (out?.response || JSON.stringify(out, null, 2));
                          const fname = `report-${analysisResult.fileName || 'result'}`.replace(/\.[^.]+$/, '');
                          if (opt.id === 'txt') {
                            const blob = new Blob([rawText], { type: 'text/plain' });
                            const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `${fname}.txt`; a.click(); URL.revokeObjectURL(url);
                            toast.success('Downloaded as TXT');
                          } else {
                            try {
                              const payload = structured ? { structured, format: opt.id, filename: fname } : { content: rawText, format: opt.id, filename: fname };
                              const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/automations/export-result`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload) }, orgId);
                              if (res.ok) { const blob = await res.blob(); const url = URL.createObjectURL(blob); const a = document.createElement('a'); a.href = url; a.download = `${fname}.${opt.id}`; a.click(); URL.revokeObjectURL(url); toast.success(`Downloaded as ${opt.label}`); }
                              else { toast.error('Export failed'); }
                            } catch { toast.error('Export failed'); }
                          }
                        }}
                        className="flex flex-col items-center gap-2 px-4 py-4 rounded-xl border border-[rgba(255,255,255,0.08)] bg-[rgba(255,255,255,0.02)] hover:border-[rgba(0,229,192,0.25)] hover:bg-[rgba(0,229,192,0.03)] transition-all group"
                      >
                        <opt.icon size={22} weight="duotone" style={{ color: opt.color }} />
                        <span className="text-[12px] font-semibold text-[#F0F4F8] group-hover:text-[#00E5C0] transition-colors">{opt.label}</span>
                        <span className="text-[10px] text-[#5C6778]">{opt.desc}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Results content */}
              <div className="px-6 pb-4 overflow-y-auto flex-1">
                <div className="rounded-xl border border-[rgba(255,255,255,0.06)] bg-[rgba(255,255,255,0.015)] p-4">
                  <AnalysisResultView output={analysisResult.output} maxHeight={500} />
                </div>
              </div>

              {/* Footer actions */}
              <div className="px-6 py-4 border-t border-[rgba(255,255,255,0.05)] bg-[rgba(0,0,0,0.15)] shrink-0">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] text-[#3A4556]">
                    {analysisResult.runId && <span>Run: {analysisResult.runId}</span>}
                  </p>
                  <div className="flex items-center gap-2">
                    {/* Re-run with different file */}
                    {runtimeMode === 'file' && (
                      <button
                        onClick={() => {
                          setAnalysisResult(null);
                          setTestFile(null);
                          setShowTestInput(true);
                        }}
                        data-testid="rerun-different-file-btn"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-[#00E5C0] hover:text-white border border-[rgba(0,229,192,0.15)] hover:border-[rgba(0,229,192,0.3)] bg-[rgba(0,229,192,0.04)] hover:bg-[rgba(0,229,192,0.08)] transition-all"
                      >
                        <ArrowsClockwise size={13} /> Re-run with different file
                      </button>
                    )}
                    {/* Copy */}
                    <button
                      onClick={() => {
                        const out = analysisResult.output;
                        const text = typeof out === 'string' ? out : (out?.response || JSON.stringify(out, null, 2));
                        navigator.clipboard.writeText(text);
                        toast.success('Copied to clipboard');
                      }}
                      data-testid="copy-result-btn"
                      className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-[#B0B8C4] hover:text-white border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)] bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.04)] transition-all"
                    >
                      <ClipboardText size={13} /> Copy
                    </button>
                    {/* Export dropdown */}
                    <div className="relative" data-testid="export-dropdown-wrap">
                      <button
                        onClick={() => setShowExportMenu(prev => !prev)}
                        data-testid="export-dropdown-btn"
                        className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-[11px] font-medium text-[#B0B8C4] hover:text-white border border-[rgba(255,255,255,0.08)] hover:border-[rgba(255,255,255,0.15)] bg-[rgba(255,255,255,0.02)] hover:bg-[rgba(255,255,255,0.04)] transition-all"
                      >
                        <DownloadSimple size={13} /> Export
                        <CaretDown size={10} />
                      </button>
                      {showExportMenu && (
                        <div className="absolute bottom-full right-0 mb-1 w-44 bg-[#1E2836] border border-[rgba(255,255,255,0.1)] rounded-xl shadow-2xl py-1 z-10">
                          {[
                            { id: 'txt', label: 'Plain Text (.txt)', icon: '📄' },
                            { id: 'pdf', label: 'PDF Document (.pdf)', icon: '📕' },
                            { id: 'docx', label: 'Word Document (.docx)', icon: '📘' },
                          ].map(opt => (
                            <button
                              key={opt.id}
                              data-testid={`export-${opt.id}`}
                              onClick={async () => {
                                setShowExportMenu(false);
                                const out = analysisResult.output;
                                const structured = typeof out === 'object' ? out?.structured : null;
                                const rawText = typeof out === 'string' ? out : (out?.response || JSON.stringify(out, null, 2));
                                const fname = `analysis-${analysisResult.fileName || 'result'}`.replace(/\.[^.]+$/, '');
                                if (opt.id === 'txt') {
                                  const blob = new Blob([rawText], { type: 'text/plain' });
                                  const url = URL.createObjectURL(blob);
                                  const a = document.createElement('a'); a.href = url; a.download = `${fname}.txt`; a.click(); URL.revokeObjectURL(url);
                                } else {
                                  try {
                                    const payload = structured
                                      ? { structured, format: opt.id, filename: fname }
                                      : { content: rawText, format: opt.id, filename: fname };
                                    const res = await fetchWithOrg(`${BACKEND_URL}/api/service-os/automations/export-result`, {
                                      method: 'POST',
                                      headers: { 'Content-Type': 'application/json' },
                                      body: JSON.stringify(payload),
                                    }, orgId);
                                    if (res.ok) {
                                      const blob = await res.blob();
                                      const url = URL.createObjectURL(blob);
                                      const a = document.createElement('a'); a.href = url; a.download = `${fname}.${opt.id}`; a.click(); URL.revokeObjectURL(url);
                                      toast.success(`Exported as ${opt.id.toUpperCase()}`);
                                    } else {
                                      toast.error('Export failed');
                                    }
                                  } catch { toast.error('Export failed'); }
                                }
                              }}
                              className="w-full flex items-center gap-2.5 px-3.5 py-2.5 text-left text-[11px] text-[#B0B8C4] hover:text-white hover:bg-[rgba(255,255,255,0.04)] transition-colors"
                            >
                              <span className="text-sm">{opt.icon}</span>
                              {opt.label}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    {/* Done */}
                    <button
                      onClick={() => setAnalysisResult(null)}
                      data-testid="close-result-btn"
                      className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-[11px] font-semibold text-[#0A1018] bg-[#00E5C0] hover:bg-[#00D4B1] transition-all shadow-[0_2px_8px_rgba(0,229,192,0.15)]"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* ═══════ DEPLOY & SHARE MODAL ═══════ */}
      <DeploymentModal
        agentId={workerId}
        agentName={worker.name}
        isOpen={showDeployModal}
        onClose={() => setShowDeployModal(false)}
      />

      {/* ═══════ RUN AGENT MODAL (for linked agents) ═══════ */}
      {linkedAgentId && (
        <RunAgentModal
          agent={{ id: linkedAgentId, agent_id: linkedAgentId, name: worker.name }}
          isOpen={showRunAgentModal}
          onClose={() => setShowRunAgentModal(false)}
          workerId={workerId}
          outcomeCategory={worker.outcome_category}
          onSuccess={() => fetchExecutions()}
        />
      )}

      {/* ═══════ CHAT CONVERSATION — For chat/event workers ═══════ */}
      <ChatWorkerModal
        workerId={workerId}
        workerName={worker.name}
        workerIcon={getWorkerIcon(worker?.outcome_category, worker?.name)}
        orgId={orgId}
        isOpen={showTestEnquiry}
        onClose={() => setShowTestEnquiry(false)}
      />

      <SetupRequiredModal
        isOpen={showSetupRequired}
        onClose={() => setShowSetupRequired(false)}
      />

      <ActivationPanel activationData={activationData} onDismiss={dismissActivation} />
    </div>
  );
}
